import React, { useState } from "react";
import { Award, Clock, Coins, ChevronRight, Check, AlertCircle } from "lucide-react";
import { applyBoost, GAME_BOOST } from "../lib/tamagotchiUtils";

const DiagnosePatient = ({ onBack, userData, saveUserData }) => {
  const [gamePhase, setGamePhase] = useState("difficulty"); // difficulty, organ-select, case-intro, history, physical, labs, imaging, diagnosis, results
  const [selectedDifficulty, setSelectedDifficulty] = useState(null);
  const [orderedCases, setOrderedCases] = useState([]);
  const [currentCase, setCurrentCase] = useState(null);
  const [currentCaseIndex, setCurrentCaseIndex] = useState(0);
  const [completedCasesByDifficulty, setCompletedCasesByDifficulty] = useState({
    easy: [],
    medium: [],
    hard: [],
  });
  const [revealedHistory, setRevealedHistory] = useState([]);
  const [revealedPhysical, setRevealedPhysical] = useState([]);
  const [orderedLabs, setOrderedLabs] = useState([]);
  const [orderedImaging, setOrderedImaging] = useState([]);
  const [pointsUsed, setPointsUsed] = useState(0);
  const [selectedDiagnosis, setSelectedDiagnosis] = useState(null);
  const [startTime, setStartTime] = useState(null);

  const difficulties = [
    {
      id: "easy",
      name: "Easy",
      description: "Common conditions with clear presentations",
      color: "from-green-100 to-emerald-100",
      borderColor: "border-green-300",
      textColor: "text-green-700",
      coins: 20,
      xp: 40,
    },
    {
      id: "medium",
      name: "Medium",
      description: "Moderately complex cases requiring thorough evaluation",
      color: "from-yellow-100 to-amber-100",
      borderColor: "border-yellow-300",
      textColor: "text-yellow-700",
      coins: 30,
      xp: 60,
    },
    {
      id: "hard",
      name: "Hard",
      description: "Challenging diagnoses with atypical presentations",
      color: "from-red-100 to-rose-100",
      borderColor: "border-red-300",
      textColor: "text-red-700",
      coins: 50,
      xp: 100,
    },
  ];

  const medicalCases = {
    easy: [
      {
        id: "appendicitis",
        organ: "Appendix",
        specialty: "General Surgery",
        chiefComplaint: "A 25-year-old patient presents with abdominal pain",
        initialPresentation: [
          "Patient reports pain that started around the belly button",
          "Pain has been present for about 12 hours",
          "Patient feels nauseous and has no appetite",
        ],
        history: [
          { id: "pain-migration", text: "Pain migrated to right lower quadrant after a few hours", cost: 5 },
          { id: "fever", text: "Low-grade fever (38.2°C)", cost: 5 },
          { id: "vomiting", text: "Vomited twice this morning", cost: 5 },
        ],
        physical: [
          { id: "mcburney", text: "Tenderness at McBurney's point", cost: 10 },
          { id: "rebound", text: "Rebound tenderness present", cost: 10 },
          { id: "rovsing", text: "Positive Rovsing's sign", cost: 10 },
        ],
        labs: [
          { id: "wbc", text: "WBC: 15,000/μL (elevated)", cost: 15 },
          { id: "crp", text: "CRP: 45 mg/L (elevated)", cost: 15 },
        ],
        imaging: [
          { id: "ct-abdomen", text: "CT Abdomen: Appendix diameter 12mm with periappendiceal fat stranding", cost: 25 },
          { id: "ultrasound", text: "Ultrasound: Non-compressible appendix", cost: 20 },
        ],
        correctDiagnosis: "Acute Appendicitis",
        differentialDiagnoses: [
          "Acute Appendicitis",
          "Gastroenteritis",
          "Ovarian Cyst Rupture",
          "Urinary Tract Infection",
        ],
        explanation: "Classic presentation of acute appendicitis with periumbilical pain migrating to the right lower quadrant, positive McBurney's point tenderness, and imaging confirmation of inflamed appendix.",
        essentialFindings: ["mcburney", "ct-abdomen"],
      },
      {
        id: "pneumonia",
        organ: "Lung",
        specialty: "Pulmonology",
        chiefComplaint: "A 45-year-old patient presents with cough and fever",
        initialPresentation: [
          "Patient has had a productive cough for 3 days",
          "High fever (39.5°C) started 2 days ago",
          "Feeling short of breath with exertion",
        ],
        history: [
          { id: "sputum", text: "Coughing up yellow-green sputum", cost: 5 },
          { id: "pleuritic", text: "Sharp chest pain when breathing deeply", cost: 5 },
          { id: "chills", text: "Experiencing chills and night sweats", cost: 5 },
        ],
        physical: [
          { id: "crackles", text: "Crackles heard in right lower lung field", cost: 10 },
          { id: "dullness", text: "Dullness to percussion on right side", cost: 10 },
          { id: "bronchial", text: "Bronchial breath sounds present", cost: 10 },
        ],
        labs: [
          { id: "wbc-pna", text: "WBC: 18,000/μL with left shift", cost: 15 },
          { id: "procalcitonin", text: "Procalcitonin: 2.5 ng/mL (elevated)", cost: 15 },
        ],
        imaging: [
          { id: "cxr", text: "Chest X-ray: Right lower lobe consolidation", cost: 20 },
          { id: "ct-chest", text: "CT Chest: Confirms lobar pneumonia", cost: 25 },
        ],
        correctDiagnosis: "Community-Acquired Pneumonia",
        differentialDiagnoses: [
          "Community-Acquired Pneumonia",
          "Bronchitis",
          "Pulmonary Embolism",
          "Lung Abscess",
        ],
        explanation: "Community-acquired pneumonia with typical presentation of productive cough, fever, and consolidation on imaging. Clinical and radiographic findings confirm bacterial pneumonia.",
        essentialFindings: ["crackles", "cxr"],
      },
      {
        id: "acute-cholecystitis",
        organ: "Gallbladder",
        specialty: "Gastroenterology",
        chiefComplaint: "A 38-year-old patient presents with right upper quadrant abdominal pain",
        initialPresentation: [
          "Pain started after a fatty meal and has persisted for 10 hours",
          "Nausea with one episode of vomiting",
          "Low-grade fever (38.1°C)",
        ],
        history: [
          { id: "fatty-foods", text: "Pain worsens after greasy foods", cost: 5 },
          { id: "prior-biliary-colic", text: "Similar but milder episodes in the past", cost: 5 },
          { id: "radiates-shoulder", text: "Pain radiates to right shoulder", cost: 5 },
        ],
        physical: [
          { id: "murphy", text: "Positive Murphy's sign on inspiration", cost: 10 },
          { id: "ruq-tender", text: "Right upper quadrant tenderness with guarding", cost: 10 },
          { id: "mild-icterus", text: "Mild scleral icterus", cost: 8 },
        ],
        labs: [
          { id: "wbc-chole", text: "WBC: 13,500/μL (elevated)", cost: 15 },
          { id: "alk-phos", text: "Alkaline phosphatase: Mildly elevated", cost: 15 },
          { id: "bilirubin", text: "Total bilirubin: 1.8 mg/dL", cost: 15 },
        ],
        imaging: [
          { id: "ruq-us", text: "RUQ Ultrasound: Gallstones with gallbladder wall thickening", cost: 20 },
          { id: "hida", text: "HIDA scan: Non-visualization of gallbladder", cost: 25 },
        ],
        correctDiagnosis: "Acute Cholecystitis",
        differentialDiagnoses: [
          "Acute Cholecystitis",
          "Biliary Colic",
          "Acute Pancreatitis",
          "Hepatitis",
        ],
        explanation: "Acute cholecystitis with RUQ pain, fever, positive Murphy's sign, and ultrasound showing gallstones with wall thickening. HIDA scan confirms cystic duct obstruction.",
        essentialFindings: ["murphy", "ruq-us"],
      },
      {
        id: "uti",
        organ: "Bladder",
        specialty: "Urology",
        chiefComplaint: "A 30-year-old patient presents with painful urination",
        initialPresentation: [
          "Burning with urination for 2 days",
          "Increased urinary frequency",
          "Lower abdominal discomfort",
        ],
        history: [
          { id: "urgency", text: "Strong urgency to urinate", cost: 5 },
          { id: "hematuria", text: "Trace blood in urine noticed", cost: 5 },
          { id: "no-flank", text: "No flank pain or fevers", cost: 5 },
        ],
        physical: [
          { id: "suprapubic-tender", text: "Mild suprapubic tenderness", cost: 10 },
          { id: "no-cva", text: "No CVA tenderness", cost: 8 },
          { id: "afebrile", text: "Afebrile on exam", cost: 8 },
        ],
        labs: [
          { id: "ua-leuk", text: "Urinalysis: Leukocyte esterase positive", cost: 15 },
          { id: "ua-nitrite", text: "Urinalysis: Nitrites positive", cost: 15 },
          { id: "urine-culture", text: "Urine culture: E. coli >100,000 CFU/mL", cost: 15 },
        ],
        imaging: [
          { id: "no-imaging", text: "Imaging not indicated for uncomplicated cystitis", cost: 5 },
        ],
        correctDiagnosis: "Acute Uncomplicated Cystitis",
        differentialDiagnoses: [
          "Acute Uncomplicated Cystitis",
          "Pyelonephritis",
          "Urethritis",
          "Interstitial Cystitis",
        ],
        explanation: "Uncomplicated cystitis with dysuria, frequency, and UA showing leukocyte esterase and nitrites. No flank pain or fever suggests lower tract infection.",
        essentialFindings: ["ua-leuk", "ua-nitrite"],
      },
      {
        id: "otitis-media",
        organ: "Ear",
        specialty: "Pediatrics",
        chiefComplaint: "A 6-year-old patient presents with ear pain and fever",
        initialPresentation: [
          "Ear pain started yesterday",
          "Low-grade fever and irritability",
          "Decreased hearing on the right",
        ],
        history: [
          { id: "recent-uri", text: "Recent runny nose and cough", cost: 5 },
          { id: "ear-pulling", text: "Parent noticed frequent ear pulling", cost: 5 },
          { id: "no-otorrhea", text: "No ear drainage", cost: 5 },
        ],
        physical: [
          { id: "bulging-tm", text: "Bulging, erythematous tympanic membrane", cost: 10 },
          { id: "decreased-mobility", text: "Decreased tympanic membrane mobility", cost: 10 },
          { id: "tender-tragus", text: "Mild tragal tenderness", cost: 8 },
        ],
        labs: [
          { id: "no-labs-otitis", text: "No labs needed for uncomplicated otitis media", cost: 5 },
        ],
        imaging: [
          { id: "no-imaging-otitis", text: "Imaging not indicated", cost: 5 },
        ],
        correctDiagnosis: "Acute Otitis Media",
        differentialDiagnoses: [
          "Acute Otitis Media",
          "Otitis Externa",
          "Viral Upper Respiratory Infection",
          "Eustachian Tube Dysfunction",
        ],
        explanation: "Acute otitis media with bulging erythematous tympanic membrane and decreased mobility following a viral URI.",
        essentialFindings: ["bulging-tm"],
      },
      {
        id: "gerd",
        organ: "Esophagus",
        specialty: "Gastroenterology",
        chiefComplaint: "A 29-year-old patient presents with heartburn",
        initialPresentation: [
          "Burning chest discomfort after meals",
          "Sour taste in the mouth",
          "Symptoms worse when lying down",
        ],
        history: [
          { id: "meal-related", text: "Symptoms worse after spicy or fatty foods", cost: 5 },
          { id: "regurgitation", text: "Acid regurgitation at night", cost: 5 },
          { id: "no-weight-loss", text: "No weight loss or dysphagia", cost: 5 },
        ],
        physical: [
          { id: "normal-exam", text: "Normal cardiopulmonary and abdominal exam", cost: 8 },
        ],
        labs: [
          { id: "no-labs-gerd", text: "No labs indicated for uncomplicated GERD", cost: 5 },
        ],
        imaging: [
          { id: "egd", text: "EGD: Mild esophagitis", cost: 20 },
          { id: "ppi-trial", text: "Symptom improvement with PPI trial", cost: 10 },
        ],
        correctDiagnosis: "Gastroesophageal Reflux Disease",
        differentialDiagnoses: [
          "Gastroesophageal Reflux Disease",
          "Peptic Ulcer Disease",
          "Angina",
          "Esophageal Spasm",
        ],
        explanation: "Typical GERD symptoms with postprandial heartburn and regurgitation, improved with PPI and mild esophagitis on EGD.",
        essentialFindings: ["regurgitation", "ppi-trial"],
      },
      {
        id: "appendicitis-early",
        organ: "Appendix",
        specialty: "General Surgery",
        chiefComplaint: "A 19-year-old patient presents with abdominal pain",
        initialPresentation: [
          "Pain started near the umbilicus 6 hours ago",
          "Mild nausea without vomiting",
          "Appetite is decreased",
        ],
        history: [
          { id: "rlq-migration", text: "Pain is now localizing to the right lower quadrant", cost: 5 },
          { id: "low-grade-fever", text: "Low-grade fever reported at home", cost: 5 },
          { id: "no-diarrhea", text: "No diarrhea or urinary symptoms", cost: 5 },
        ],
        physical: [
          { id: "rlq-tender", text: "Mild tenderness in the right lower quadrant", cost: 10 },
          { id: "psoas", text: "Pain with passive hip extension (psoas sign)", cost: 10 },
          { id: "guarding", text: "Mild voluntary guarding", cost: 8 },
        ],
        labs: [
          { id: "wbc-mild", text: "WBC: 12,200/μL (mildly elevated)", cost: 15 },
          { id: "crp-mild", text: "CRP: 18 mg/L (elevated)", cost: 15 },
        ],
        imaging: [
          { id: "us-appendix", text: "Ultrasound: Non-compressible appendix with diameter 9 mm", cost: 20 },
          { id: "ct-early", text: "CT Abdomen: Mild appendiceal wall thickening", cost: 25 },
        ],
        correctDiagnosis: "Early Acute Appendicitis",
        differentialDiagnoses: [
          "Early Acute Appendicitis",
          "Gastroenteritis",
          "Mesenteric Adenitis",
          "Ovarian Torsion",
        ],
        explanation: "Early appendicitis with periumbilical pain migrating to RLQ, mild leukocytosis, and ultrasound showing a non-compressible appendix.",
        essentialFindings: ["rlq-migration", "us-appendix"],
      },
      {
        id: "asthma-exacerbation",
        organ: "Lung",
        specialty: "Pulmonology",
        chiefComplaint: "A 22-year-old patient presents with wheezing and shortness of breath",
        initialPresentation: [
          "Wheezing started overnight",
          "Tightness in the chest",
          "Using rescue inhaler more often",
        ],
        history: [
          { id: "allergy-trigger", text: "Symptoms worsened after exposure to dust", cost: 5 },
          { id: "night-cough", text: "Cough worse at night", cost: 5 },
          { id: "no-fever", text: "No fever or sputum production", cost: 5 },
        ],
        physical: [
          { id: "diffuse-wheeze", text: "Diffuse expiratory wheezes", cost: 10 },
          { id: "prolonged-exp", text: "Prolonged expiratory phase", cost: 8 },
          { id: "mild-retractions", text: "Mild accessory muscle use", cost: 8 },
        ],
        labs: [
          { id: "peak-flow", text: "Peak expiratory flow: 60% predicted", cost: 15 },
        ],
        imaging: [
          { id: "cxr-normal", text: "Chest X-ray: No infiltrate", cost: 20 },
        ],
        correctDiagnosis: "Asthma Exacerbation",
        differentialDiagnoses: [
          "Asthma Exacerbation",
          "Acute Bronchitis",
          "Pneumonia",
          "Vocal Cord Dysfunction",
        ],
        explanation: "Asthma exacerbation with wheezing, chest tightness, and reduced peak flow without infectious signs.",
        essentialFindings: ["diffuse-wheeze", "peak-flow"],
      },
      {
        id: "biliary-colic",
        organ: "Gallbladder",
        specialty: "Gastroenterology",
        chiefComplaint: "A 40-year-old patient presents with episodic right upper quadrant pain",
        initialPresentation: [
          "Pain occurs after fatty meals",
          "Episodes last 1-2 hours",
          "No fever or vomiting",
        ],
        history: [
          { id: "meal-trigger", text: "Pain begins within an hour of eating", cost: 5 },
          { id: "nausea-mild", text: "Mild nausea during episodes", cost: 5 },
          { id: "no-icterus", text: "No jaundice noticed", cost: 5 },
        ],
        physical: [
          { id: "ruq-tender-mild", text: "Mild RUQ tenderness", cost: 10 },
          { id: "no-guarding", text: "No guarding or rebound", cost: 8 },
          { id: "afebrile-colic", text: "Afebrile on exam", cost: 8 },
        ],
        labs: [
          { id: "normal-lfts", text: "LFTs: Within normal limits", cost: 15 },
          { id: "normal-wbc", text: "WBC: Normal", cost: 15 },
        ],
        imaging: [
          { id: "ruq-us-colic", text: "RUQ Ultrasound: Gallstones without wall thickening", cost: 20 },
        ],
        correctDiagnosis: "Biliary Colic",
        differentialDiagnoses: [
          "Biliary Colic",
          "Acute Cholecystitis",
          "Peptic Ulcer Disease",
          "Hepatitis",
        ],
        explanation: "Biliary colic with postprandial RUQ pain, normal labs, and ultrasound showing gallstones without inflammation.",
        essentialFindings: ["meal-trigger", "ruq-us-colic"],
      },
      {
        id: "interstitial-cystitis",
        organ: "Bladder",
        specialty: "Urology",
        chiefComplaint: "A 34-year-old patient presents with chronic pelvic pain and urinary urgency",
        initialPresentation: [
          "Suprapubic pain for 4 months",
          "Urinary frequency and urgency",
          "Pain improves slightly after voiding",
        ],
        history: [
          { id: "pain-flares", text: "Symptoms flare with stress and certain foods", cost: 5 },
          { id: "no-fever-ic", text: "No fevers or chills", cost: 5 },
          { id: "prior-antibiotics", text: "No improvement with prior antibiotics", cost: 5 },
        ],
        physical: [
          { id: "suprapubic-tender-ic", text: "Suprapubic tenderness", cost: 10 },
          { id: "normal-cva", text: "No CVA tenderness", cost: 8 },
          { id: "no-discharge", text: "No urethral discharge", cost: 8 },
        ],
        labs: [
          { id: "negative-ua", text: "Urinalysis: No leukocytes or nitrites", cost: 15 },
          { id: "culture-negative", text: "Urine culture: No growth", cost: 15 },
        ],
        imaging: [
          { id: "cystoscopy", text: "Cystoscopy: Glomerulations on bladder wall", cost: 25 },
        ],
        correctDiagnosis: "Interstitial Cystitis",
        differentialDiagnoses: [
          "Interstitial Cystitis",
          "Recurrent UTI",
          "Overactive Bladder",
          "Endometriosis",
        ],
        explanation: "Interstitial cystitis with chronic suprapubic pain, urgency, negative cultures, and cystoscopic findings.",
        essentialFindings: ["suprapubic-tender-ic", "negative-ua"],
      },
      {
        id: "otitis-externa",
        organ: "Ear",
        specialty: "ENT",
        chiefComplaint: "A 17-year-old patient presents with ear pain after swimming",
        initialPresentation: [
          "Ear pain worsens when the ear is touched",
          "Itching in the ear canal",
          "Muffled hearing on the left",
        ],
        history: [
          { id: "recent-swim", text: "Swam daily over the weekend", cost: 5 },
          { id: "no-fever-oe", text: "No fever or URI symptoms", cost: 5 },
          { id: "ear-drops", text: "Tried over-the-counter ear drops without relief", cost: 5 },
        ],
        physical: [
          { id: "tragus-pain", text: "Pain with tragus manipulation", cost: 10 },
          { id: "canal-edema", text: "Erythematous, edematous ear canal", cost: 10 },
          { id: "tm-normal", text: "Tympanic membrane appears normal", cost: 8 },
        ],
        labs: [
          { id: "no-labs-oe", text: "No labs required", cost: 5 },
        ],
        imaging: [
          { id: "no-imaging-oe", text: "Imaging not indicated", cost: 5 },
        ],
        correctDiagnosis: "Otitis Externa",
        differentialDiagnoses: [
          "Otitis Externa",
          "Acute Otitis Media",
          "Temporomandibular Joint Pain",
          "Mastoiditis",
        ],
        explanation: "Otitis externa with pain on tragus manipulation and an edematous ear canal after swimming.",
        essentialFindings: ["tragus-pain", "canal-edema"],
      },
      {
        id: "reflux-esophagitis",
        organ: "Esophagus",
        specialty: "Gastroenterology",
        chiefComplaint: "A 33-year-old patient presents with painful swallowing",
        initialPresentation: [
          "Odynophagia for 1 week",
          "Burning substernal discomfort",
          "Symptoms worse after meals",
        ],
        history: [
          { id: "acid-reflux", text: "Frequent acid reflux for several months", cost: 5 },
          { id: "no-weight-loss-reflux", text: "No weight loss or vomiting", cost: 5 },
          { id: "night-symptoms", text: "Symptoms worse at night when lying flat", cost: 5 },
        ],
        physical: [
          { id: "benign-exam", text: "Normal oral and abdominal exam", cost: 8 },
        ],
        labs: [
          { id: "no-labs-reflux", text: "No labs indicated", cost: 5 },
        ],
        imaging: [
          { id: "egd-esophagitis", text: "EGD: Distal esophageal erythema and erosions", cost: 20 },
        ],
        correctDiagnosis: "Reflux Esophagitis",
        differentialDiagnoses: [
          "Reflux Esophagitis",
          "Pill Esophagitis",
          "Infectious Esophagitis",
          "Esophageal Spasm",
        ],
        explanation: "Reflux esophagitis with odynophagia and endoscopic erosions in the distal esophagus.",
        essentialFindings: ["acid-reflux", "egd-esophagitis"],
      },
      {
        id: "strep-pharyngitis",
        organ: "Throat",
        specialty: "Pediatrics",
        chiefComplaint: "A 10-year-old patient presents with sore throat and fever",
        initialPresentation: [
          "Sore throat for 2 days",
          "Fever up to 38.9°C",
          "Painful swallowing",
        ],
        history: [
          { id: "abrupt-onset", text: "Symptoms started suddenly", cost: 5 },
          { id: "no-cough", text: "No cough or runny nose", cost: 5 },
          { id: "sick-contact", text: "Classmate recently had strep throat", cost: 5 },
        ],
        physical: [
          { id: "tonsillar-exudate", text: "Tonsillar exudates present", cost: 10 },
          { id: "cervical-lymph", text: "Tender anterior cervical lymphadenopathy", cost: 10 },
          { id: "no-conjunctivitis", text: "No conjunctivitis or rhinorrhea", cost: 8 },
        ],
        labs: [
          { id: "rapid-strep", text: "Rapid strep test: Positive", cost: 15 },
          { id: "throat-culture", text: "Throat culture: Group A streptococcus", cost: 15 },
        ],
        imaging: [
          { id: "no-imaging-strep", text: "Imaging not indicated", cost: 5 },
        ],
        correctDiagnosis: "Streptococcal Pharyngitis",
        differentialDiagnoses: [
          "Streptococcal Pharyngitis",
          "Viral Pharyngitis",
          "Infectious Mononucleosis",
          "Peritonsillar Abscess",
        ],
        explanation: "Strep pharyngitis with tonsillar exudates, cervical lymphadenopathy, fever, and absence of cough or coryza. Centor criteria support testing and positive rapid strep confirms diagnosis.",
        essentialFindings: ["tonsillar-exudate", "rapid-strep"],
      },
      {
        id: "iron-deficiency-anemia",
        organ: "Blood",
        specialty: "Hematology",
        chiefComplaint: "A 28-year-old woman presents with fatigue and shortness of breath",
        initialPresentation: [
          "Progressive fatigue over 3 months",
          "Shortness of breath with exertion",
          "Heavy menstrual periods",
        ],
        history: [
          { id: "heavy-menses", text: "Menstrual periods lasting 7-8 days with heavy flow", cost: 5 },
          { id: "pica", text: "Craving ice chips", cost: 5 },
          { id: "diet", text: "Vegetarian diet with limited iron-rich foods", cost: 5 },
        ],
        physical: [
          { id: "pallor-ida", text: "Conjunctival pallor", cost: 10 },
          { id: "koilonychia", text: "Spoon-shaped nails", cost: 10 },
          { id: "tachycardia-ida", text: "Mild tachycardia", cost: 8 },
        ],
        labs: [
          { id: "low-hgb", text: "Hemoglobin: 8.2 g/dL (low)", cost: 15 },
          { id: "low-mcv", text: "MCV: 68 fL (microcytic)", cost: 15 },
          { id: "low-ferritin", text: "Ferritin: 8 ng/mL (low)", cost: 15 },
        ],
        imaging: [
          { id: "no-imaging-ida", text: "Imaging not indicated for uncomplicated IDA", cost: 5 },
        ],
        correctDiagnosis: "Iron Deficiency Anemia",
        differentialDiagnoses: [
          "Iron Deficiency Anemia",
          "Thalassemia Trait",
          "Anemia of Chronic Disease",
          "Lead Poisoning",
        ],
        explanation: "Iron deficiency anemia with microcytic anemia, low ferritin, and heavy menstrual blood loss as the likely cause.",
        essentialFindings: ["low-mcv", "low-ferritin"],
      },
      {
        id: "cellulitis",
        organ: "Skin",
        specialty: "Dermatology",
        chiefComplaint: "A 55-year-old patient presents with a red, swollen leg",
        initialPresentation: [
          "Redness and swelling of the left lower leg",
          "Warmth and pain in the area",
          "Fever started yesterday",
        ],
        history: [
          { id: "skin-break", text: "Small cut from gardening 3 days ago", cost: 5 },
          { id: "diabetes", text: "History of diabetes mellitus", cost: 5 },
          { id: "spread", text: "Redness has been spreading", cost: 5 },
        ],
        physical: [
          { id: "erythema", text: "Well-demarcated erythema with warmth", cost: 10 },
          { id: "edema-cellulitis", text: "Pitting edema of the leg", cost: 10 },
          { id: "no-crepitus", text: "No crepitus or fluctuance", cost: 8 },
        ],
        labs: [
          { id: "wbc-cellulitis", text: "WBC: 14,000/μL (elevated)", cost: 15 },
          { id: "crp-cellulitis", text: "CRP: 65 mg/L (elevated)", cost: 15 },
        ],
        imaging: [
          { id: "no-imaging-cellulitis", text: "Imaging not needed for uncomplicated cellulitis", cost: 5 },
        ],
        correctDiagnosis: "Cellulitis",
        differentialDiagnoses: [
          "Cellulitis",
          "Deep Vein Thrombosis",
          "Erysipelas",
          "Necrotizing Fasciitis",
        ],
        explanation: "Cellulitis with spreading erythema, warmth, edema, and systemic signs of infection following a skin break. No features of necrotizing infection.",
        essentialFindings: ["erythema", "skin-break"],
      },
      {
        id: "migraine",
        organ: "Brain",
        specialty: "Neurology",
        chiefComplaint: "A 26-year-old woman presents with severe headache",
        initialPresentation: [
          "Throbbing headache on the right side",
          "Nausea and sensitivity to light",
          "Had similar headaches before",
        ],
        history: [
          { id: "aura", text: "Saw flashing lights before headache started", cost: 5 },
          { id: "triggers", text: "Headache started during stressful week at work", cost: 5 },
          { id: "family-migraine", text: "Mother has migraines", cost: 5 },
        ],
        physical: [
          { id: "photophobia-exam", text: "Photophobia on examination", cost: 10 },
          { id: "neuro-normal", text: "Normal neurological examination", cost: 10 },
          { id: "no-fever-migraine", text: "Afebrile", cost: 8 },
        ],
        labs: [
          { id: "no-labs-migraine", text: "Labs not indicated for typical migraine", cost: 5 },
        ],
        imaging: [
          { id: "no-imaging-migraine", text: "Imaging not needed with typical presentation", cost: 5 },
        ],
        correctDiagnosis: "Migraine with Aura",
        differentialDiagnoses: [
          "Migraine with Aura",
          "Tension-Type Headache",
          "Cluster Headache",
          "Intracranial Mass",
        ],
        explanation: "Migraine with aura presenting with unilateral throbbing headache, visual aura, photophobia, and nausea. Normal neurological exam and family history support the diagnosis.",
        essentialFindings: ["aura", "photophobia-exam"],
      },
    ],
    medium: [
      {
        id: "diabetic-ketoacidosis",
        organ: "Endocrine",
        specialty: "Endocrinology",
        chiefComplaint: "A 28-year-old patient with known diabetes presents feeling very unwell",
        initialPresentation: [
          "Patient with Type 1 diabetes ran out of insulin 2 days ago",
          "Feeling extremely tired and weak",
          "Increased thirst and urination",
        ],
        history: [
          { id: "abdominal-pain", text: "Generalized abdominal pain", cost: 5 },
          { id: "nausea-vomit", text: "Nausea and vomiting multiple times", cost: 5 },
          { id: "fruity-breath", text: "Family noticed fruity smell on breath", cost: 5 },
          { id: "confusion", text: "Increasingly confused over past 6 hours", cost: 8 },
        ],
        physical: [
          { id: "kussmaul", text: "Deep, labored breathing (Kussmaul respirations)", cost: 10 },
          { id: "dehydration", text: "Signs of severe dehydration - dry mucous membranes, poor skin turgor", cost: 10 },
          { id: "tachycardia", text: "Heart rate 120 bpm", cost: 8 },
        ],
        labs: [
          { id: "glucose", text: "Blood glucose: 450 mg/dL", cost: 15 },
          { id: "ketones", text: "Serum ketones: 5.2 mmol/L (high)", cost: 15 },
          { id: "ph", text: "Arterial pH: 7.15 (acidotic)", cost: 15 },
          { id: "bicarb", text: "Bicarbonate: 10 mEq/L (low)", cost: 15 },
        ],
        imaging: [
          { id: "cxr-dka", text: "Chest X-ray: Clear lung fields", cost: 20 },
        ],
        correctDiagnosis: "Diabetic Ketoacidosis",
        differentialDiagnoses: [
          "Diabetic Ketoacidosis",
          "Hyperosmolar Hyperglycemic State",
          "Sepsis",
          "Acute Pancreatitis",
        ],
        explanation: "Diabetic ketoacidosis (DKA) presenting with the classic triad of hyperglycemia, ketosis, and metabolic acidosis. Precipitated by insulin omission in a Type 1 diabetic patient.",
        essentialFindings: ["kussmaul", "glucose", "ketones", "ph"],
      },
      {
        id: "pulmonary-embolism",
        organ: "Vascular",
        specialty: "Emergency Medicine",
        chiefComplaint: "A 55-year-old patient presents with sudden onset shortness of breath",
        initialPresentation: [
          "Sudden difficulty breathing started 2 hours ago",
          "Recent long-haul flight (10 hours) 3 days ago",
          "Sharp chest pain that worsens with deep breathing",
        ],
        history: [
          { id: "leg-swelling", text: "Left leg swelling noticed yesterday", cost: 5 },
          { id: "hemoptysis", text: "Small amount of blood in sputum", cost: 8 },
          { id: "anxiety", text: "Feeling anxious and sense of impending doom", cost: 5 },
        ],
        physical: [
          { id: "tachypnea", text: "Respiratory rate: 28/min", cost: 10 },
          { id: "o2-sat", text: "Oxygen saturation: 88% on room air", cost: 10 },
          { id: "dvt-signs", text: "Left calf swelling, warmth, and tenderness", cost: 10 },
        ],
        labs: [
          { id: "d-dimer", text: "D-dimer: 2,500 ng/mL (markedly elevated)", cost: 15 },
          { id: "troponin", text: "Troponin: Mildly elevated", cost: 15 },
          { id: "abg", text: "ABG: PaO2 65 mmHg, respiratory alkalosis", cost: 15 },
        ],
        imaging: [
          { id: "cta", text: "CT Angiography: Filling defects in right pulmonary artery branches", cost: 30 },
          { id: "leg-doppler", text: "Doppler Ultrasound: Deep vein thrombosis in left femoral vein", cost: 25 },
        ],
        correctDiagnosis: "Pulmonary Embolism",
        differentialDiagnoses: [
          "Pulmonary Embolism",
          "Myocardial Infarction",
          "Pneumothorax",
          "Pneumonia",
        ],
        explanation: "Pulmonary embolism with source DVT following prolonged immobilization. Classic presentation with sudden dyspnea, pleuritic chest pain, and confirmatory imaging showing thrombus in pulmonary vasculature.",
        essentialFindings: ["d-dimer", "cta"],
      },
      {
        id: "acute-pancreatitis",
        organ: "Pancreas",
        specialty: "Gastroenterology",
        chiefComplaint: "A 48-year-old patient presents with severe epigastric pain",
        initialPresentation: [
          "Severe epigastric pain radiating to the back",
          "Pain started suddenly and is constant",
          "Multiple episodes of vomiting",
        ],
        history: [
          { id: "alcohol-binge", text: "Heavy alcohol intake over the weekend", cost: 5 },
          { id: "gallstones-history", text: "Known history of gallstones", cost: 5 },
          { id: "relief-leaning", text: "Pain improves slightly when leaning forward", cost: 5 },
        ],
        physical: [
          { id: "epigastric-tender", text: "Marked epigastric tenderness", cost: 10 },
          { id: "abdominal-distension", text: "Mild abdominal distension", cost: 8 },
          { id: "hypoactive-bowel", text: "Hypoactive bowel sounds", cost: 8 },
        ],
        labs: [
          { id: "lipase", text: "Lipase: 1,200 U/L (elevated)", cost: 15 },
          { id: "amylase", text: "Amylase: 780 U/L (elevated)", cost: 15 },
          { id: "calcium", text: "Calcium: 7.8 mg/dL (low)", cost: 15 },
        ],
        imaging: [
          { id: "ct-pancreas", text: "CT Abdomen: Enlarged pancreas with peripancreatic fat stranding", cost: 25 },
          { id: "us-gallstones", text: "Ultrasound: Gallstones without CBD dilation", cost: 20 },
        ],
        correctDiagnosis: "Acute Pancreatitis",
        differentialDiagnoses: [
          "Acute Pancreatitis",
          "Peptic Ulcer Disease",
          "Acute Cholecystitis",
          "Small Bowel Obstruction",
        ],
        explanation: "Acute pancreatitis suggested by severe epigastric pain radiating to the back and elevated lipase. CT imaging shows pancreatic inflammation.",
        essentialFindings: ["epigastric-tender", "lipase"],
      },
      {
        id: "nephrolithiasis",
        organ: "Kidney",
        specialty: "Nephrology",
        chiefComplaint: "A 41-year-old patient presents with severe flank pain",
        initialPresentation: [
          "Sudden onset left flank pain radiating to the groin",
          "Pain is colicky and comes in waves",
          "Nausea without vomiting",
        ],
        history: [
          { id: "stone-history", text: "Prior history of kidney stones", cost: 5 },
          { id: "low-fluid", text: "Poor fluid intake over past week", cost: 5 },
          { id: "gross-heme", text: "Noticed pink urine today", cost: 5 },
        ],
        physical: [
          { id: "cva-tender", text: "Left CVA tenderness", cost: 10 },
          { id: "restless", text: "Restless due to pain", cost: 8 },
          { id: "abd-soft", text: "Abdomen soft without peritonitis", cost: 8 },
        ],
        labs: [
          { id: "ua-heme", text: "Urinalysis: Microscopic hematuria", cost: 15 },
          { id: "creatinine", text: "Creatinine: Normal", cost: 15 },
        ],
        imaging: [
          { id: "ct-stone", text: "CT KUB: 4 mm distal ureteral stone", cost: 25 },
          { id: "us-hydro", text: "Renal ultrasound: Mild hydronephrosis", cost: 20 },
        ],
        correctDiagnosis: "Ureteral Stone",
        differentialDiagnoses: [
          "Ureteral Stone",
          "Pyelonephritis",
          "Musculoskeletal Pain",
          "Abdominal Aortic Aneurysm",
        ],
        explanation: "Nephrolithiasis presenting with colicky flank pain and hematuria. CT KUB confirms a distal ureteral stone.",
        essentialFindings: ["ua-heme", "ct-stone"],
      },
      {
        id: "hepatitis-a",
        organ: "Liver",
        specialty: "Infectious Disease",
        chiefComplaint: "A 26-year-old patient presents with fatigue and jaundice",
        initialPresentation: [
          "Dark urine and pale stools for 3 days",
          "Nausea and loss of appetite",
          "Recent international travel",
        ],
        history: [
          { id: "travel", text: "Traveled to region with poor sanitation 4 weeks ago", cost: 5 },
          { id: "no-etoh", text: "No heavy alcohol use", cost: 5 },
          { id: "no-med-change", text: "No new medications or supplements", cost: 5 },
        ],
        physical: [
          { id: "icterus", text: "Scleral icterus present", cost: 10 },
          { id: "hepatomegaly", text: "Mildly enlarged, tender liver", cost: 10 },
          { id: "no-ascites", text: "No ascites", cost: 8 },
        ],
        labs: [
          { id: "ast-alt", text: "AST/ALT: Markedly elevated (AST 980, ALT 1200)", cost: 15 },
          { id: "bilirubin-high", text: "Total bilirubin: 6.2 mg/dL", cost: 15 },
          { id: "hav-igm", text: "HAV IgM: Positive", cost: 20 },
        ],
        imaging: [
          { id: "ruq-us-normal", text: "RUQ Ultrasound: No biliary obstruction", cost: 20 },
        ],
        correctDiagnosis: "Acute Hepatitis A",
        differentialDiagnoses: [
          "Acute Hepatitis A",
          "Acute Hepatitis B",
          "Drug-Induced Hepatitis",
          "Choledocholithiasis",
        ],
        explanation: "Acute hepatitis A with travel exposure, jaundice, markedly elevated transaminases, and positive HAV IgM.",
        essentialFindings: ["ast-alt", "hav-igm"],
      },
      {
        id: "crohns",
        organ: "Intestine",
        specialty: "Gastroenterology",
        chiefComplaint: "A 24-year-old patient presents with chronic diarrhea and abdominal pain",
        initialPresentation: [
          "Watery diarrhea for 6 weeks",
          "Crampy right lower quadrant pain",
          "Unintentional weight loss",
        ],
        history: [
          { id: "chronic-diarrhea", text: "Diarrhea 4-6 times per day", cost: 5 },
          { id: "oral-ulcers", text: "Intermittent mouth ulcers", cost: 5 },
          { id: "family-ibd", text: "Family history of IBD", cost: 5 },
        ],
        physical: [
          { id: "rlq-tender", text: "Right lower quadrant tenderness", cost: 10 },
          { id: "perianal", text: "Perianal fissures noted", cost: 10 },
          { id: "low-bmi", text: "Low BMI and muscle wasting", cost: 8 },
        ],
        labs: [
          { id: "crp-high", text: "CRP: Elevated", cost: 15 },
          { id: "anemia", text: "Hemoglobin: 9.8 g/dL (anemia)", cost: 15 },
        ],
        imaging: [
          { id: "colonoscopy-skip", text: "Colonoscopy: Skip lesions with cobblestoning", cost: 25 },
          { id: "mri-enterography", text: "MRI Enterography: Terminal ileitis", cost: 25 },
        ],
        correctDiagnosis: "Crohn Disease",
        differentialDiagnoses: [
          "Crohn Disease",
          "Ulcerative Colitis",
          "Irritable Bowel Syndrome",
          "Celiac Disease",
        ],
        explanation: "Crohn disease with chronic diarrhea, RLQ pain, perianal disease, and colonoscopy showing skip lesions.",
        essentialFindings: ["chronic-diarrhea", "colonoscopy-skip"],
      },
      {
        id: "graves-disease",
        organ: "Endocrine",
        specialty: "Endocrinology",
        chiefComplaint: "A 31-year-old patient presents with weight loss and palpitations",
        initialPresentation: [
          "Unintentional weight loss despite increased appetite",
          "Heat intolerance and sweating",
          "Tremor and anxiety",
        ],
        history: [
          { id: "palpitations", text: "Frequent palpitations", cost: 5 },
          { id: "menstrual", text: "Irregular menses", cost: 5 },
          { id: "family-thyroid", text: "Family history of thyroid disease", cost: 5 },
        ],
        physical: [
          { id: "tremor", text: "Fine tremor of hands", cost: 10 },
          { id: "goiter", text: "Diffuse, non-tender goiter", cost: 10 },
          { id: "eye-findings", text: "Mild exophthalmos", cost: 10 },
        ],
        labs: [
          { id: "low-tsh", text: "TSH: Suppressed", cost: 15 },
          { id: "high-t4", text: "Free T4: Elevated", cost: 15 },
          { id: "trab", text: "TSH receptor antibodies: Positive", cost: 20 },
        ],
        imaging: [
          { id: "uptake-scan", text: "Radioiodine uptake: Diffuse increased uptake", cost: 25 },
        ],
        correctDiagnosis: "Graves Disease",
        differentialDiagnoses: [
          "Graves Disease",
          "Toxic Multinodular Goiter",
          "Thyroiditis",
          "Anxiety Disorder",
        ],
        explanation: "Graves disease with hyperthyroid symptoms, suppressed TSH, elevated T4, positive TRAb, and diffuse uptake.",
        essentialFindings: ["low-tsh", "trab"],
      },
      {
        id: "dvt",
        organ: "Vascular",
        specialty: "Vascular Medicine",
        chiefComplaint: "A 52-year-old patient presents with unilateral leg swelling",
        initialPresentation: [
          "Left calf swelling and pain for 2 days",
          "Recent knee surgery 1 week ago",
          "No chest pain or shortness of breath",
        ],
        history: [
          { id: "recent-surgery", text: "Recent orthopedic surgery with immobilization", cost: 5 },
          { id: "no-anticoag", text: "Not on anticoagulation", cost: 5 },
          { id: "no-infection", text: "No fever or skin infection", cost: 5 },
        ],
        physical: [
          { id: "leg-swelling-dvt", text: "Left calf swelling and tenderness", cost: 10 },
          { id: "warmth", text: "Increased warmth over calf", cost: 8 },
          { id: "no-erythema", text: "Minimal erythema", cost: 8 },
        ],
        labs: [
          { id: "d-dimer-dvt", text: "D-dimer: Elevated", cost: 15 },
        ],
        imaging: [
          { id: "venous-us", text: "Compression ultrasound: Non-compressible femoral vein", cost: 25 },
        ],
        correctDiagnosis: "Deep Vein Thrombosis",
        differentialDiagnoses: [
          "Deep Vein Thrombosis",
          "Cellulitis",
          "Baker Cyst",
          "Muscle Strain",
        ],
        explanation: "DVT with unilateral calf swelling after surgery and confirmatory venous ultrasound.",
        essentialFindings: ["leg-swelling-dvt", "venous-us"],
      },
      {
        id: "chronic-pancreatitis",
        organ: "Pancreas",
        specialty: "Gastroenterology",
        chiefComplaint: "A 50-year-old patient presents with chronic epigastric pain",
        initialPresentation: [
          "Recurrent epigastric pain for 1 year",
          "Steatorrhea and weight loss",
          "History of heavy alcohol use",
        ],
        history: [
          { id: "alcohol-years", text: "Long-term alcohol use", cost: 5 },
          { id: "fatty-stools", text: "Greasy, foul-smelling stools", cost: 5 },
          { id: "worsens-meals", text: "Pain worsens after meals", cost: 5 },
        ],
        physical: [
          { id: "epigastric-tender-chronic", text: "Epigastric tenderness without guarding", cost: 10 },
          { id: "weight-loss", text: "Unintentional weight loss", cost: 8 },
          { id: "no-fever", text: "Afebrile", cost: 8 },
        ],
        labs: [
          { id: "lipase-normal", text: "Lipase: Normal or mildly elevated", cost: 15 },
          { id: "low-fecal-elastase", text: "Fecal elastase: Low", cost: 15 },
        ],
        imaging: [
          { id: "pancreatic-calcifications", text: "CT Abdomen: Pancreatic calcifications", cost: 25 },
          { id: "dilated-duct", text: "MRCP: Dilated pancreatic duct", cost: 25 },
        ],
        correctDiagnosis: "Chronic Pancreatitis",
        differentialDiagnoses: [
          "Chronic Pancreatitis",
          "Peptic Ulcer Disease",
          "Pancreatic Cancer",
          "Functional Dyspepsia",
        ],
        explanation: "Chronic pancreatitis with long-term alcohol use, steatorrhea, and pancreatic calcifications.",
        essentialFindings: ["fatty-stools", "pancreatic-calcifications"],
      },
      {
        id: "pyelonephritis",
        organ: "Kidney",
        specialty: "Nephrology",
        chiefComplaint: "A 27-year-old patient presents with flank pain and fever",
        initialPresentation: [
          "Right flank pain for 2 days",
          "Fever and chills",
          "Nausea and vomiting",
        ],
        history: [
          { id: "urinary-symptoms", text: "Dysuria and frequency started last week", cost: 5 },
          { id: "no-stone-history", text: "No history of kidney stones", cost: 5 },
          { id: "pregnancy", text: "Not pregnant", cost: 5 },
        ],
        physical: [
          { id: "cva-tender-pyelo", text: "Right CVA tenderness", cost: 10 },
          { id: "fever", text: "Fever to 39 C", cost: 8 },
          { id: "tachycardia", text: "Heart rate 110 bpm", cost: 8 },
        ],
        labs: [
          { id: "ua-wbc-casts", text: "Urinalysis: WBC casts and pyuria", cost: 15 },
          { id: "urine-culture-pyelo", text: "Urine culture: E. coli", cost: 15 },
          { id: "wbc-pyelo", text: "WBC: 16,000/μL", cost: 15 },
        ],
        imaging: [
          { id: "ct-pyelo", text: "CT Abdomen: Striated nephrogram", cost: 25 },
        ],
        correctDiagnosis: "Acute Pyelonephritis",
        differentialDiagnoses: [
          "Acute Pyelonephritis",
          "Ureteral Stone",
          "Appendicitis",
          "Cholecystitis",
        ],
        explanation: "Pyelonephritis with flank pain, fever, CVA tenderness, and UA showing WBC casts.",
        essentialFindings: ["cva-tender-pyelo", "ua-wbc-casts"],
      },
      {
        id: "hepatitis-b",
        organ: "Liver",
        specialty: "Infectious Disease",
        chiefComplaint: "A 33-year-old patient presents with jaundice and fatigue",
        initialPresentation: [
          "Yellowing of eyes for 1 week",
          "Dark urine and nausea",
          "No recent travel",
        ],
        history: [
          { id: "new-partner", text: "New sexual partner in the past month", cost: 5 },
          { id: "no-vaccination", text: "Not vaccinated for hepatitis B", cost: 5 },
          { id: "no-etoh-b", text: "No heavy alcohol use", cost: 5 },
        ],
        physical: [
          { id: "icterus-b", text: "Scleral icterus", cost: 10 },
          { id: "ruq-tender-b", text: "Mild RUQ tenderness", cost: 10 },
          { id: "no-ascites-b", text: "No ascites", cost: 8 },
        ],
        labs: [
          { id: "ast-alt-b", text: "AST/ALT: Markedly elevated", cost: 15 },
          { id: "hbsag", text: "HBsAg: Positive", cost: 20 },
          { id: "anti-hbc-igm", text: "Anti-HBc IgM: Positive", cost: 20 },
        ],
        imaging: [
          { id: "ruq-us-b", text: "RUQ Ultrasound: Normal biliary tree", cost: 20 },
        ],
        correctDiagnosis: "Acute Hepatitis B",
        differentialDiagnoses: [
          "Acute Hepatitis B",
          "Acute Hepatitis A",
          "Drug-Induced Hepatitis",
          "Autoimmune Hepatitis",
        ],
        explanation: "Acute hepatitis B with jaundice, elevated transaminases, and positive HBsAg and anti-HBc IgM.",
        essentialFindings: ["hbsag", "anti-hbc-igm"],
      },
      {
        id: "ulcerative-colitis",
        organ: "Intestine",
        specialty: "Gastroenterology",
        chiefComplaint: "A 29-year-old patient presents with bloody diarrhea",
        initialPresentation: [
          "Bloody diarrhea for 3 weeks",
          "Left lower quadrant cramping",
          "Urgency and tenesmus",
        ],
        history: [
          { id: "bloody-diarrhea", text: "Blood mixed with stool", cost: 5 },
          { id: "no-travel", text: "No recent travel or sick contacts", cost: 5 },
          { id: "no-antibiotics", text: "No recent antibiotic use", cost: 5 },
        ],
        physical: [
          { id: "llq-tender", text: "Left lower quadrant tenderness", cost: 10 },
          { id: "mild-fever", text: "Low-grade fever", cost: 8 },
          { id: "pallor", text: "Mild pallor", cost: 8 },
        ],
        labs: [
          { id: "crp-uc", text: "CRP: Elevated", cost: 15 },
          { id: "anemia-uc", text: "Hemoglobin: 10.2 g/dL", cost: 15 },
        ],
        imaging: [
          { id: "colonoscopy-continuous", text: "Colonoscopy: Continuous mucosal inflammation from rectum", cost: 25 },
        ],
        correctDiagnosis: "Ulcerative Colitis",
        differentialDiagnoses: [
          "Ulcerative Colitis",
          "Crohn Disease",
          "Infectious Colitis",
          "Ischemic Colitis",
        ],
        explanation: "Ulcerative colitis with bloody diarrhea, LLQ pain, and continuous inflammation starting at the rectum.",
        essentialFindings: ["bloody-diarrhea", "colonoscopy-continuous"],
      },
      {
        id: "acute-mi",
        organ: "Heart",
        specialty: "Cardiology",
        chiefComplaint: "A 58-year-old man presents with crushing chest pain",
        initialPresentation: [
          "Crushing substernal chest pain for 45 minutes",
          "Pain radiates to left arm and jaw",
          "Diaphoresis and nausea",
        ],
        history: [
          { id: "cardiac-risk", text: "History of hypertension and smoking", cost: 5 },
          { id: "exertional", text: "Similar but milder pain with exertion in past", cost: 5 },
          { id: "no-relief", text: "Pain not relieved by rest or antacids", cost: 5 },
        ],
        physical: [
          { id: "diaphoresis", text: "Diaphoretic and anxious appearance", cost: 10 },
          { id: "s4-gallop", text: "S4 gallop heard", cost: 10 },
          { id: "stable-bp", text: "Blood pressure 150/90 mmHg", cost: 8 },
        ],
        labs: [
          { id: "troponin-elevated", text: "Troponin I: Elevated and rising", cost: 20 },
          { id: "bnp", text: "BNP: Mildly elevated", cost: 15 },
        ],
        imaging: [
          { id: "ecg-stemi", text: "ECG: ST elevations in leads V1-V4", cost: 25 },
          { id: "echo-mi", text: "Echo: Anterior wall motion abnormality", cost: 25 },
        ],
        correctDiagnosis: "Acute ST-Elevation Myocardial Infarction",
        differentialDiagnoses: [
          "Acute ST-Elevation Myocardial Infarction",
          "Unstable Angina",
          "Aortic Dissection",
          "Pericarditis",
        ],
        explanation: "STEMI with typical chest pain, ST elevations on ECG, elevated troponin, and wall motion abnormality. Immediate reperfusion is indicated.",
        essentialFindings: ["troponin-elevated", "ecg-stemi"],
      },
      {
        id: "copd-exacerbation",
        organ: "Lung",
        specialty: "Pulmonology",
        chiefComplaint: "A 65-year-old patient presents with worsening shortness of breath",
        initialPresentation: [
          "Increasing dyspnea over 3 days",
          "Increased sputum production",
          "Long history of smoking",
        ],
        history: [
          { id: "smoking-copd", text: "50 pack-year smoking history", cost: 5 },
          { id: "purulent-sputum", text: "Sputum has become yellow-green", cost: 5 },
          { id: "prior-exacerbations", text: "Multiple prior hospitalizations for COPD", cost: 5 },
        ],
        physical: [
          { id: "barrel-chest", text: "Barrel-shaped chest", cost: 10 },
          { id: "decreased-breath", text: "Decreased breath sounds bilaterally with wheezes", cost: 10 },
          { id: "accessory-muscles", text: "Using accessory muscles to breathe", cost: 10 },
        ],
        labs: [
          { id: "abg-copd", text: "ABG: pH 7.32, pCO2 55, pO2 58", cost: 20 },
          { id: "wbc-copd", text: "WBC: 12,000/μL", cost: 15 },
        ],
        imaging: [
          { id: "cxr-hyperinflation", text: "Chest X-ray: Hyperinflation, flattened diaphragms", cost: 20 },
        ],
        correctDiagnosis: "Acute COPD Exacerbation",
        differentialDiagnoses: [
          "Acute COPD Exacerbation",
          "Pneumonia",
          "Heart Failure",
          "Pulmonary Embolism",
        ],
        explanation: "COPD exacerbation with increased dyspnea, sputum production, and sputum purulence. ABG shows respiratory acidosis with hypoxemia.",
        essentialFindings: ["purulent-sputum", "abg-copd"],
      },
      {
        id: "heart-failure",
        organ: "Heart",
        specialty: "Cardiology",
        chiefComplaint: "A 70-year-old patient presents with leg swelling and shortness of breath",
        initialPresentation: [
          "Progressive leg swelling over 2 weeks",
          "Shortness of breath when lying flat",
          "Waking up at night gasping for air",
        ],
        history: [
          { id: "htn-hf", text: "History of poorly controlled hypertension", cost: 5 },
          { id: "prior-mi", text: "Had a heart attack 5 years ago", cost: 5 },
          { id: "weight-gain", text: "Gained 10 pounds in 2 weeks", cost: 5 },
        ],
        physical: [
          { id: "jvd", text: "Jugular venous distension", cost: 10 },
          { id: "crackles-hf", text: "Bibasilar crackles", cost: 10 },
          { id: "pitting-edema", text: "2+ pitting edema of bilateral lower extremities", cost: 10 },
        ],
        labs: [
          { id: "bnp-high", text: "BNP: 1,200 pg/mL (markedly elevated)", cost: 20 },
          { id: "creatinine-hf", text: "Creatinine: 1.4 mg/dL (mildly elevated)", cost: 15 },
        ],
        imaging: [
          { id: "cxr-hf", text: "Chest X-ray: Cardiomegaly with pulmonary vascular congestion", cost: 20 },
          { id: "echo-ef", text: "Echo: EF 30%, dilated LV", cost: 25 },
        ],
        correctDiagnosis: "Acute Decompensated Heart Failure",
        differentialDiagnoses: [
          "Acute Decompensated Heart Failure",
          "Chronic Kidney Disease",
          "Cirrhosis",
          "Nephrotic Syndrome",
        ],
        explanation: "Decompensated heart failure with orthopnea, PND, JVD, peripheral edema, elevated BNP, and reduced ejection fraction on echo.",
        essentialFindings: ["jvd", "bnp-high", "echo-ef"],
      },
      {
        id: "hypothyroidism",
        organ: "Endocrine",
        specialty: "Endocrinology",
        chiefComplaint: "A 45-year-old woman presents with fatigue and weight gain",
        initialPresentation: [
          "Fatigue and sluggishness for 4 months",
          "Weight gain despite no change in diet",
          "Feeling cold all the time",
        ],
        history: [
          { id: "constipation", text: "Constipation has worsened", cost: 5 },
          { id: "hair-loss", text: "Hair thinning and dry skin", cost: 5 },
          { id: "depression", text: "Feeling depressed", cost: 5 },
        ],
        physical: [
          { id: "bradycardia", text: "Heart rate 54 bpm", cost: 10 },
          { id: "dry-skin", text: "Dry, coarse skin", cost: 8 },
          { id: "delayed-reflexes", text: "Delayed relaxation of deep tendon reflexes", cost: 10 },
        ],
        labs: [
          { id: "high-tsh", text: "TSH: 45 mIU/L (markedly elevated)", cost: 15 },
          { id: "low-t4", text: "Free T4: 0.4 ng/dL (low)", cost: 15 },
          { id: "anti-tpo", text: "Anti-TPO antibodies: Positive", cost: 15 },
        ],
        imaging: [
          { id: "no-imaging-thyroid", text: "Imaging not indicated", cost: 5 },
        ],
        correctDiagnosis: "Hashimoto's Hypothyroidism",
        differentialDiagnoses: [
          "Hashimoto's Hypothyroidism",
          "Depression",
          "Anemia",
          "Sleep Apnea",
        ],
        explanation: "Primary hypothyroidism with elevated TSH, low free T4, and positive anti-TPO antibodies consistent with Hashimoto's thyroiditis.",
        essentialFindings: ["high-tsh", "anti-tpo"],
      },
    ],
    hard: [
      {
        id: "meningitis",
        organ: "Brain",
        specialty: "Neurology",
        chiefComplaint: "A 20-year-old college student presents with severe headache and fever",
        initialPresentation: [
          "Severe headache started suddenly 8 hours ago",
          "High fever (40°C) and feeling very ill",
          "Sensitivity to bright lights",
        ],
        history: [
          { id: "neck-stiff", text: "Neck feels very stiff and painful to move", cost: 5 },
          { id: "rash", text: "Non-blanching petechial rash appeared on legs", cost: 8 },
          { id: "vomiting-severe", text: "Projectile vomiting several times", cost: 5 },
          { id: "confusion-severe", text: "Increasingly confused and disoriented", cost: 8 },
        ],
        physical: [
          { id: "brudzinski", text: "Positive Brudzinski's sign", cost: 10 },
          { id: "kernig", text: "Positive Kernig's sign", cost: 10 },
          { id: "photophobia", text: "Severe photophobia on exam", cost: 8 },
          { id: "petechiae", text: "Petechial rash non-blanching on extremities", cost: 10 },
        ],
        labs: [
          { id: "wbc-high", text: "WBC: 25,000/μL", cost: 15 },
          { id: "csf-analysis", text: "CSF: WBC 2,500/μL (90% neutrophils), protein 180 mg/dL, glucose 25 mg/dL", cost: 25 },
          { id: "blood-culture", text: "Blood cultures: Pending (later positive for N. meningitidis)", cost: 20 },
        ],
        imaging: [
          { id: "ct-head", text: "CT Head: No mass lesion, normal ventricles", cost: 25 },
        ],
        correctDiagnosis: "Bacterial Meningitis",
        differentialDiagnoses: [
          "Bacterial Meningitis",
          "Viral Meningitis",
          "Subarachnoid Hemorrhage",
          "Encephalitis",
        ],
        explanation: "Bacterial meningitis (likely meningococcal) with classic triad of fever, headache, and neck stiffness. The petechial rash and CSF findings confirm the diagnosis. This is a medical emergency requiring immediate antibiotics.",
        essentialFindings: ["brudzinski", "kernig", "csf-analysis"],
      },
      {
        id: "sle",
        organ: "Immune",
        specialty: "Rheumatology",
        chiefComplaint: "A 32-year-old woman presents with joint pain and fatigue",
        initialPresentation: [
          "Joint pain in hands and knees for 3 months",
          "Extreme fatigue despite adequate sleep",
          "Intermittent low-grade fevers",
        ],
        history: [
          { id: "malar-rash", text: "Facial rash across cheeks and nose, worsens with sun exposure", cost: 8 },
          { id: "photosensitivity", text: "Skin becomes red and painful after sun exposure", cost: 5 },
          { id: "oral-ulcers", text: "Recurrent painless mouth sores", cost: 5 },
          { id: "raynaud", text: "Fingers turn white then blue in cold weather", cost: 5 },
          { id: "hair-loss", text: "Significant hair thinning over past 2 months", cost: 5 },
        ],
        physical: [
          { id: "butterfly-rash", text: "Malar (butterfly) rash visible on face", cost: 10 },
          { id: "joint-swelling", text: "Symmetric swelling of MCP and PIP joints", cost: 10 },
          { id: "pleural-rub", text: "Pleural friction rub heard on auscultation", cost: 10 },
        ],
        labs: [
          { id: "ana", text: "ANA: Positive (1:640, speckled pattern)", cost: 20 },
          { id: "anti-dsdna", text: "Anti-dsDNA antibodies: Strongly positive", cost: 20 },
          { id: "anti-sm", text: "Anti-Smith antibodies: Positive", cost: 20 },
          { id: "complement", text: "C3 and C4 complement levels: Low", cost: 15 },
          { id: "urinalysis", text: "Urinalysis: Proteinuria and RBC casts", cost: 15 },
        ],
        imaging: [
          { id: "cxr-sle", text: "Chest X-ray: Small bilateral pleural effusions", cost: 20 },
          { id: "echo", text: "Echocardiogram: Small pericardial effusion", cost: 25 },
        ],
        correctDiagnosis: "Systemic Lupus Erythematosus",
        differentialDiagnoses: [
          "Systemic Lupus Erythematosus",
          "Rheumatoid Arthritis",
          "Mixed Connective Tissue Disease",
          "Drug-Induced Lupus",
        ],
        explanation: "Systemic Lupus Erythematosus (SLE) with multiple organ involvement including skin (malar rash), joints (arthritis), kidneys (proteinuria, RBC casts), and serositis (pleural and pericardial effusions). Positive ANA, anti-dsDNA, and anti-Smith antibodies confirm the diagnosis.",
        essentialFindings: ["butterfly-rash", "ana", "anti-dsdna", "urinalysis"],
      },
      {
        id: "aortic-dissection",
        organ: "Aorta",
        specialty: "Cardiology",
        chiefComplaint: "A 62-year-old patient presents with sudden severe chest pain",
        initialPresentation: [
          "Tearing chest pain radiating to the back",
          "Pain began abruptly while at rest",
          "Feels lightheaded and sweaty",
        ],
        history: [
          { id: "htn", text: "Long history of poorly controlled hypertension", cost: 5 },
          { id: "connective-tissue", text: "Family history of Marfan syndrome", cost: 8 },
          { id: "neuro-symptoms", text: "Brief episode of right arm weakness", cost: 8 },
        ],
        physical: [
          { id: "bp-diff", text: "Blood pressure differential between arms", cost: 10 },
          { id: "new-murmur", text: "New diastolic murmur of aortic regurgitation", cost: 10 },
          { id: "pulse-deficit", text: "Diminished left radial pulse", cost: 10 },
        ],
        labs: [
          { id: "d-dimer-dissection", text: "D-dimer: Elevated", cost: 15 },
          { id: "troponin-dissection", text: "Troponin: Normal", cost: 15 },
        ],
        imaging: [
          { id: "cxr-mediastinum", text: "Chest X-ray: Widened mediastinum", cost: 20 },
          { id: "cta-aorta", text: "CT Angiography: Intimal flap in ascending aorta", cost: 30 },
        ],
        correctDiagnosis: "Aortic Dissection",
        differentialDiagnoses: [
          "Aortic Dissection",
          "Acute Coronary Syndrome",
          "Pulmonary Embolism",
          "Pericarditis",
        ],
        explanation: "Aortic dissection with classic tearing chest pain, pulse/BP differential, and CTA showing an intimal flap. Immediate surgical evaluation is required for ascending dissections.",
        essentialFindings: ["bp-diff", "cta-aorta"],
      },
      {
        id: "myasthenia-gravis",
        organ: "Neuromuscular",
        specialty: "Neurology",
        chiefComplaint: "A 36-year-old patient presents with fluctuating muscle weakness",
        initialPresentation: [
          "Ptosis and double vision worse in the evening",
          "Difficulty chewing and swallowing by the end of meals",
          "Symptoms improve with rest",
        ],
        history: [
          { id: "fatigable", text: "Weakness worsens with repetitive activity", cost: 5 },
          { id: "no-sensory", text: "No numbness or sensory changes", cost: 5 },
          { id: "recent-infection", text: "Recent upper respiratory infection", cost: 5 },
        ],
        physical: [
          { id: "ptosis", text: "Bilateral ptosis on sustained upgaze", cost: 10 },
          { id: "fatigue-test", text: "Strength declines with repetitive testing", cost: 10 },
          { id: "reflexes", text: "Reflexes intact", cost: 8 },
        ],
        labs: [
          { id: "achr", text: "AChR antibodies: Positive", cost: 20 },
          { id: "tsh", text: "TSH: Normal", cost: 15 },
        ],
        imaging: [
          { id: "ct-thymus", text: "CT Chest: Anterior mediastinal mass consistent with thymoma", cost: 25 },
        ],
        correctDiagnosis: "Myasthenia Gravis",
        differentialDiagnoses: [
          "Myasthenia Gravis",
          "Lambert-Eaton Syndrome",
          "Multiple Sclerosis",
          "Botulism",
        ],
        explanation: "Myasthenia gravis with fatigable weakness, ocular symptoms, positive AChR antibodies, and thymic mass on imaging.",
        essentialFindings: ["ptosis", "achr"],
      },
      {
        id: "pheochromocytoma",
        organ: "Adrenal",
        specialty: "Endocrinology",
        chiefComplaint: "A 40-year-old patient presents with episodic headaches and palpitations",
        initialPresentation: [
          "Severe pounding headaches lasting 10-15 minutes",
          "Palpitations and sweating during episodes",
          "Blood pressure spikes noted at home",
        ],
        history: [
          { id: "episodic", text: "Episodes occur several times per week", cost: 5 },
          { id: "family-history", text: "Family history of MEN2", cost: 8 },
          { id: "weight-loss", text: "Unintentional weight loss", cost: 5 },
        ],
        physical: [
          { id: "htn-paroxysmal", text: "Paroxysmal hypertension", cost: 10 },
          { id: "diaphoresis", text: "Profuse diaphoresis during episode", cost: 8 },
          { id: "tachycardia-episode", text: "Tachycardia noted during exam", cost: 8 },
        ],
        labs: [
          { id: "metanephrines", text: "Plasma free metanephrines: Elevated", cost: 20 },
          { id: "catecholamines", text: "24-hour urine catecholamines: Elevated", cost: 20 },
        ],
        imaging: [
          { id: "adrenal-ct", text: "CT Abdomen: Adrenal mass 3.5 cm", cost: 25 },
          { id: "mibg", text: "MIBG scan: Increased uptake in adrenal lesion", cost: 25 },
        ],
        correctDiagnosis: "Pheochromocytoma",
        differentialDiagnoses: [
          "Pheochromocytoma",
          "Panic Disorder",
          "Hyperthyroidism",
          "Carcinoid Syndrome",
        ],
        explanation: "Pheochromocytoma with episodic headaches, palpitations, diaphoresis, and elevated metanephrines with adrenal mass on imaging.",
        essentialFindings: ["metanephrines", "adrenal-ct"],
      },
      {
        id: "aplastic-anemia",
        organ: "Bone Marrow",
        specialty: "Hematology",
        chiefComplaint: "A 29-year-old patient presents with fatigue and easy bruising",
        initialPresentation: [
          "Progressive fatigue over 2 months",
          "Frequent nosebleeds",
          "Bruising with minimal trauma",
        ],
        history: [
          { id: "med-exposure", text: "Recent exposure to chloramphenicol", cost: 5 },
          { id: "no-lymphadenopathy", text: "No lymph node swelling", cost: 5 },
          { id: "recurrent-infections", text: "Multiple recent infections", cost: 5 },
        ],
        physical: [
          { id: "petechiae", text: "Diffuse petechiae and ecchymoses", cost: 10 },
          { id: "pallor", text: "Marked pallor", cost: 8 },
          { id: "no-hepatosplenomegaly", text: "No hepatosplenomegaly", cost: 8 },
        ],
        labs: [
          { id: "pancytopenia", text: "CBC: Pancytopenia (WBC 1.8, Hb 7.4, Plt 18)", cost: 20 },
          { id: "retic-low", text: "Reticulocyte count: Low", cost: 15 },
        ],
        imaging: [
          { id: "bm-biopsy", text: "Bone marrow biopsy: Hypocellular marrow", cost: 25 },
        ],
        correctDiagnosis: "Aplastic Anemia",
        differentialDiagnoses: [
          "Aplastic Anemia",
          "Acute Leukemia",
          "Myelodysplastic Syndrome",
          "Hypersplenism",
        ],
        explanation: "Aplastic anemia with pancytopenia, low reticulocytes, and hypocellular marrow, without organomegaly.",
        essentialFindings: ["pancytopenia", "bm-biopsy"],
      },
      {
        id: "subarachnoid-hemorrhage",
        organ: "Brain",
        specialty: "Neurology",
        chiefComplaint: "A 48-year-old patient presents with sudden severe headache",
        initialPresentation: [
          "Thunderclap headache described as the worst of life",
          "Neck stiffness and photophobia",
          "Nausea and vomiting",
        ],
        history: [
          { id: "sudden-onset", text: "Headache reached maximal intensity in seconds", cost: 5 },
          { id: "no-trauma", text: "No recent trauma", cost: 5 },
          { id: "family-aneurysm", text: "Family history of cerebral aneurysm", cost: 8 },
        ],
        physical: [
          { id: "nuchal-rigidity", text: "Nuchal rigidity", cost: 10 },
          { id: "photophobia-sah", text: "Marked photophobia", cost: 8 },
          { id: "neuro-intact", text: "No focal neurologic deficits", cost: 8 },
        ],
        labs: [
          { id: "lp-xantho", text: "Lumbar puncture: Xanthochromia", cost: 20 },
        ],
        imaging: [
          { id: "ct-sah", text: "CT Head: Subarachnoid blood in basal cisterns", cost: 25 },
          { id: "cta-aneurysm", text: "CTA: Saccular aneurysm", cost: 30 },
        ],
        correctDiagnosis: "Subarachnoid Hemorrhage",
        differentialDiagnoses: [
          "Subarachnoid Hemorrhage",
          "Migraine",
          "Meningitis",
          "Intracerebral Hemorrhage",
        ],
        explanation: "Subarachnoid hemorrhage with thunderclap headache, meningeal signs, and CT showing subarachnoid blood.",
        essentialFindings: ["sudden-onset", "ct-sah"],
      },
      {
        id: "gpa",
        organ: "Immune",
        specialty: "Rheumatology",
        chiefComplaint: "A 45-year-old patient presents with cough, hematuria, and sinus symptoms",
        initialPresentation: [
          "Chronic sinusitis with nasal crusting",
          "Cough with occasional hemoptysis",
          "Dark urine and fatigue",
        ],
        history: [
          { id: "sinus-issues", text: "Recurrent sinus infections", cost: 5 },
          { id: "hemoptysis", text: "Blood-tinged sputum", cost: 5 },
          { id: "weight-loss", text: "Unintentional weight loss", cost: 5 },
        ],
        physical: [
          { id: "nasal-ulcers", text: "Nasal ulcerations", cost: 10 },
          { id: "crackles", text: "Crackles at lung bases", cost: 10 },
          { id: "skin-purpura", text: "Palpable purpura on legs", cost: 10 },
        ],
        labs: [
          { id: "c-anca", text: "c-ANCA: Positive", cost: 20 },
          { id: "urinalysis-renal", text: "Urinalysis: Hematuria with RBC casts", cost: 20 },
        ],
        imaging: [
          { id: "cavitary-nodules", text: "Chest CT: Cavitary pulmonary nodules", cost: 25 },
        ],
        correctDiagnosis: "Granulomatosis with Polyangiitis",
        differentialDiagnoses: [
          "Granulomatosis with Polyangiitis",
          "Goodpasture Syndrome",
          "Sarcoidosis",
          "Tuberculosis",
        ],
        explanation: "GPA with ENT involvement, pulmonary nodules, renal disease, and positive c-ANCA.",
        essentialFindings: ["c-anca", "cavitary-nodules"],
      },
      {
        id: "thoracic-aneurysm",
        organ: "Aorta",
        specialty: "Cardiology",
        chiefComplaint: "A 67-year-old patient presents with chest and back discomfort",
        initialPresentation: [
          "Dull chest and upper back pain",
          "Hoarseness and dry cough",
          "History of smoking",
        ],
        history: [
          { id: "smoking", text: "40 pack-year smoking history", cost: 5 },
          { id: "htn-aneurysm", text: "Long-standing hypertension", cost: 5 },
          { id: "no-syncope", text: "No syncope or neurologic deficits", cost: 5 },
        ],
        physical: [
          { id: "hoarseness", text: "Hoarse voice", cost: 10 },
          { id: "bounding-pulse", text: "Bounding pulses", cost: 8 },
          { id: "no-focal-deficit", text: "No focal neurologic deficits", cost: 8 },
        ],
        labs: [
          { id: "labs-normal-taa", text: "Basic labs: Unremarkable", cost: 15 },
        ],
        imaging: [
          { id: "cta-aneurysm-thoracic", text: "CTA: Ascending thoracic aortic aneurysm 5.8 cm", cost: 30 },
          { id: "cxr-widened", text: "Chest X-ray: Widened mediastinum", cost: 20 },
        ],
        correctDiagnosis: "Thoracic Aortic Aneurysm",
        differentialDiagnoses: [
          "Thoracic Aortic Aneurysm",
          "Aortic Dissection",
          "Lung Cancer",
          "Pericarditis",
        ],
        explanation: "Thoracic aortic aneurysm with hoarseness, mediastinal widening, and CTA confirming aneurysm size.",
        essentialFindings: ["hoarseness", "cta-aneurysm-thoracic"],
      },
      {
        id: "guillain-barre",
        organ: "Neuromuscular",
        specialty: "Neurology",
        chiefComplaint: "A 38-year-old patient presents with progressive weakness",
        initialPresentation: [
          "Ascending weakness over 5 days",
          "Tingling in feet and hands",
          "Recent diarrheal illness",
        ],
        history: [
          { id: "recent-campy", text: "Gastroenteritis 2 weeks ago", cost: 5 },
          { id: "no-bowel-bladder", text: "No bowel or bladder dysfunction", cost: 5 },
          { id: "progressive", text: "Weakness is rapidly progressive", cost: 5 },
        ],
        physical: [
          { id: "areflexia", text: "Absent deep tendon reflexes", cost: 10 },
          { id: "symmetric-weakness", text: "Symmetric proximal weakness", cost: 10 },
          { id: "sensory-mild", text: "Mild sensory loss", cost: 8 },
        ],
        labs: [
          { id: "csf-protein", text: "CSF: Elevated protein with normal cell count", cost: 20 },
        ],
        imaging: [
          { id: "emg", text: "EMG/NCS: Demyelinating polyneuropathy", cost: 25 },
        ],
        correctDiagnosis: "Guillain-Barre Syndrome",
        differentialDiagnoses: [
          "Guillain-Barre Syndrome",
          "Myasthenia Gravis",
          "Spinal Cord Compression",
          "Botulism",
        ],
        explanation: "Guillain-Barre with ascending weakness, areflexia, and CSF albuminocytologic dissociation.",
        essentialFindings: ["areflexia", "csf-protein"],
      },
      {
        id: "addisonian-crisis",
        organ: "Adrenal",
        specialty: "Endocrinology",
        chiefComplaint: "A 44-year-old patient presents with weakness and low blood pressure",
        initialPresentation: [
          "Severe fatigue and dizziness",
          "Nausea and abdominal pain",
          "Darkening of skin over months",
        ],
        history: [
          { id: "weight-loss", text: "Unintentional weight loss", cost: 5 },
          { id: "salt-craving", text: "Craving salty foods", cost: 5 },
          { id: "autoimmune", text: "History of autoimmune thyroid disease", cost: 5 },
        ],
        physical: [
          { id: "hypotension", text: "Hypotension and orthostasis", cost: 10 },
          { id: "hyperpigmentation", text: "Hyperpigmentation of skin creases", cost: 10 },
          { id: "dehydration", text: "Dry mucous membranes", cost: 8 },
        ],
        labs: [
          { id: "low-cortisol", text: "Morning cortisol: Low", cost: 20 },
          { id: "hyponatremia", text: "Sodium: 126 mEq/L", cost: 15 },
          { id: "hyperkalemia", text: "Potassium: 5.8 mEq/L", cost: 15 },
        ],
        imaging: [
          { id: "acth-test", text: "ACTH stimulation test: Blunted response", cost: 20 },
        ],
        correctDiagnosis: "Addisonian Crisis",
        differentialDiagnoses: [
          "Addisonian Crisis",
          "Sepsis",
          "Gastroenteritis",
          "Adrenal Hemorrhage",
        ],
        explanation: "Primary adrenal insufficiency with hypotension, hyperpigmentation, hyponatremia, and low cortisol.",
        essentialFindings: ["low-cortisol", "hyponatremia"],
      },
      {
        id: "acute-leukemia",
        organ: "Bone Marrow",
        specialty: "Hematology",
        chiefComplaint: "A 36-year-old patient presents with fatigue, infections, and bruising",
        initialPresentation: [
          "Progressive fatigue for 1 month",
          "Recurrent fevers",
          "Easy bruising and gum bleeding",
        ],
        history: [
          { id: "night-sweats", text: "Night sweats", cost: 5 },
          { id: "bone-pain", text: "Diffuse bone pain", cost: 5 },
          { id: "no-travel", text: "No recent travel", cost: 5 },
        ],
        physical: [
          { id: "petechiae-acute", text: "Petechiae and ecchymoses", cost: 10 },
          { id: "pallor-acute", text: "Marked pallor", cost: 8 },
          { id: "hepatosplenomegaly", text: "Hepatosplenomegaly", cost: 10 },
        ],
        labs: [
          { id: "blasts", text: "Peripheral smear: Blasts present", cost: 20 },
          { id: "low-platelets", text: "Platelets: 22,000/μL", cost: 20 },
          { id: "anemia-acute", text: "Hemoglobin: 7.8 g/dL", cost: 15 },
        ],
        imaging: [
          { id: "flow-cytometry", text: "Flow cytometry: Myeloid blasts", cost: 25 },
        ],
        correctDiagnosis: "Acute Leukemia",
        differentialDiagnoses: [
          "Acute Leukemia",
          "Aplastic Anemia",
          "Myelodysplastic Syndrome",
          "Hypersplenism",
        ],
        explanation: "Acute leukemia with blasts on smear, cytopenias, and organomegaly.",
        essentialFindings: ["blasts", "low-platelets"],
      },
      {
        id: "infective-endocarditis",
        organ: "Heart",
        specialty: "Cardiology",
        chiefComplaint: "A 35-year-old patient with IV drug use presents with fever and fatigue",
        initialPresentation: [
          "Persistent fever for 2 weeks",
          "Night sweats and weight loss",
          "History of IV heroin use",
        ],
        history: [
          { id: "ivdu", text: "Active IV drug use without clean needles", cost: 5 },
          { id: "malaise", text: "Severe malaise and poor appetite", cost: 5 },
          { id: "embolic", text: "Noticed painful red spots on fingers", cost: 8 },
        ],
        physical: [
          { id: "new-murmur-ie", text: "New holosystolic murmur at tricuspid area", cost: 10 },
          { id: "osler-nodes", text: "Painful nodules on fingertips (Osler nodes)", cost: 10 },
          { id: "splinter-hemorrhages", text: "Splinter hemorrhages in nail beds", cost: 10 },
        ],
        labs: [
          { id: "blood-cultures", text: "Blood cultures: Staphylococcus aureus (3/3 bottles)", cost: 25 },
          { id: "esr-crp", text: "ESR and CRP: Markedly elevated", cost: 15 },
          { id: "anemia-ie", text: "Hemoglobin: 9.8 g/dL (normocytic anemia)", cost: 15 },
        ],
        imaging: [
          { id: "echo-vegetation", text: "TEE: Vegetation on tricuspid valve", cost: 30 },
          { id: "cxr-septic-emboli", text: "Chest X-ray: Multiple septic pulmonary emboli", cost: 20 },
        ],
        correctDiagnosis: "Infective Endocarditis",
        differentialDiagnoses: [
          "Infective Endocarditis",
          "Sepsis from Other Source",
          "Rheumatic Fever",
          "Atrial Myxoma",
        ],
        explanation: "Infective endocarditis meeting Duke criteria with bacteremia, new murmur, vegetations on echo, and peripheral embolic phenomena.",
        essentialFindings: ["blood-cultures", "echo-vegetation"],
      },
      {
        id: "multiple-sclerosis",
        organ: "Brain",
        specialty: "Neurology",
        chiefComplaint: "A 28-year-old woman presents with vision loss and leg weakness",
        initialPresentation: [
          "Vision loss in right eye over 3 days",
          "Pain with eye movement",
          "Leg weakness and tingling in hands",
        ],
        history: [
          { id: "prior-episode", text: "Had numbness in legs that resolved 6 months ago", cost: 8 },
          { id: "heat-worsening", text: "Symptoms worse with hot baths", cost: 5 },
          { id: "lhermitte", text: "Electric sensation down spine when bending neck", cost: 8 },
        ],
        physical: [
          { id: "optic-neuritis", text: "Reduced visual acuity OS with afferent pupillary defect", cost: 10 },
          { id: "hyperreflexia", text: "Hyperreflexia in lower extremities", cost: 10 },
          { id: "babinski", text: "Positive Babinski sign bilaterally", cost: 10 },
        ],
        labs: [
          { id: "csf-bands", text: "CSF: Oligoclonal bands present", cost: 25 },
          { id: "igg-index", text: "IgG index: Elevated", cost: 20 },
        ],
        imaging: [
          { id: "mri-demyelinating", text: "MRI Brain: Multiple periventricular white matter lesions", cost: 30 },
          { id: "mri-spine", text: "MRI Spine: Cervical cord lesion", cost: 25 },
        ],
        correctDiagnosis: "Multiple Sclerosis",
        differentialDiagnoses: [
          "Multiple Sclerosis",
          "Neuromyelitis Optica",
          "CNS Vasculitis",
          "Vitamin B12 Deficiency",
        ],
        explanation: "MS with dissemination in time and space - optic neuritis, prior sensory episode, MRI lesions in brain and spine, and CSF oligoclonal bands.",
        essentialFindings: ["optic-neuritis", "mri-demyelinating", "csf-bands"],
      },
      {
        id: "acute-liver-failure",
        organ: "Liver",
        specialty: "Hepatology",
        chiefComplaint: "A 23-year-old patient presents with confusion and jaundice",
        initialPresentation: [
          "Progressive confusion over 2 days",
          "Yellowing of eyes noted by family",
          "Took large amount of acetaminophen 3 days ago",
        ],
        history: [
          { id: "acetaminophen-od", text: "Ingested 30 grams of acetaminophen", cost: 8 },
          { id: "nausea-vomiting", text: "Severe nausea and vomiting", cost: 5 },
          { id: "rlq-pain", text: "Right upper quadrant pain", cost: 5 },
        ],
        physical: [
          { id: "altered-mental", text: "Disoriented to time and place", cost: 10 },
          { id: "asterixis", text: "Asterixis (liver flap) present", cost: 10 },
          { id: "icterus-alf", text: "Deep scleral icterus", cost: 10 },
        ],
        labs: [
          { id: "massive-ast-alt", text: "AST/ALT: >10,000 U/L", cost: 20 },
          { id: "inr-elevated", text: "INR: 4.5 (markedly elevated)", cost: 20 },
          { id: "ammonia", text: "Ammonia: 150 μmol/L (elevated)", cost: 15 },
          { id: "acetaminophen-level", text: "Acetaminophen level: Elevated", cost: 15 },
        ],
        imaging: [
          { id: "us-alf", text: "Ultrasound: Small liver, patent hepatic vessels", cost: 20 },
        ],
        correctDiagnosis: "Acute Liver Failure",
        differentialDiagnoses: [
          "Acute Liver Failure",
          "Acute Hepatitis",
          "Sepsis",
          "Alcoholic Hepatitis",
        ],
        explanation: "Acute liver failure from acetaminophen toxicity with hepatic encephalopathy, coagulopathy, and massively elevated transaminases. Urgent transplant evaluation needed.",
        essentialFindings: ["acetaminophen-od", "inr-elevated", "asterixis"],
      },
      {
        id: "thyroid-storm",
        organ: "Endocrine",
        specialty: "Endocrinology",
        chiefComplaint: "A 38-year-old patient with Graves disease presents with fever and agitation",
        initialPresentation: [
          "High fever (40.5°C) and confusion",
          "Severe agitation and tremor",
          "Stopped taking methimazole 2 weeks ago",
        ],
        history: [
          { id: "stopped-meds", text: "Stopped thyroid medication due to side effects", cost: 5 },
          { id: "recent-surgery", text: "Had dental extraction 3 days ago", cost: 8 },
          { id: "diarrhea-ts", text: "Profuse diarrhea", cost: 5 },
        ],
        physical: [
          { id: "high-fever", text: "Temperature 40.5°C", cost: 10 },
          { id: "tachycardia-ts", text: "Heart rate 160 bpm, irregular", cost: 10 },
          { id: "goiter-ts", text: "Diffuse goiter with bruit", cost: 10 },
          { id: "lid-lag", text: "Lid lag and stare", cost: 8 },
        ],
        labs: [
          { id: "suppressed-tsh", text: "TSH: <0.01 mIU/L (suppressed)", cost: 15 },
          { id: "high-t3-t4", text: "Free T4 and T3: Markedly elevated", cost: 20 },
        ],
        imaging: [
          { id: "ecg-afib", text: "ECG: Atrial fibrillation with RVR", cost: 20 },
        ],
        correctDiagnosis: "Thyroid Storm",
        differentialDiagnoses: [
          "Thyroid Storm",
          "Sepsis",
          "Neuroleptic Malignant Syndrome",
          "Drug Intoxication",
        ],
        explanation: "Thyroid storm with fever, tachycardia, altered mental status, GI symptoms, and markedly elevated thyroid hormones. Life-threatening emergency requiring immediate treatment.",
        essentialFindings: ["high-fever", "suppressed-tsh", "tachycardia-ts"],
      },
    ],
  };

  const startCaseSequence = (cases, organSelection) => {
    setOrderedCases(cases);
    setCurrentCaseIndex(0);
    setCurrentCase(cases[0]);
    setRevealedHistory([]);
    setRevealedPhysical([]);
    setOrderedLabs([]);
    setOrderedImaging([]);
    setPointsUsed(0);
    setSelectedDiagnosis(null);
    setStartTime(Date.now());
    setGamePhase("case-intro");
  };

  const handleDifficultySelect = (difficultyId) => {
    setSelectedDifficulty(difficultyId);
    setOrderedCases([]);
    setCurrentCase(null);
    setCurrentCaseIndex(0);
    setGamePhase("organ-select");
  };

  const handleRevealHistory = (historyId) => {
    const historyItem = currentCase.history.find(h => h.id === historyId);
    if (!revealedHistory.includes(historyId)) {
      setRevealedHistory([...revealedHistory, historyId]);
      setPointsUsed(pointsUsed + historyItem.cost);
    }
  };

  const handleRevealPhysical = (physicalId) => {
    const physicalItem = currentCase.physical.find(p => p.id === physicalId);
    if (!revealedPhysical.includes(physicalId)) {
      setRevealedPhysical([...revealedPhysical, physicalId]);
      setPointsUsed(pointsUsed + physicalItem.cost);
    }
  };

  const handleOrderLab = (labId) => {
    const labItem = currentCase.labs.find(l => l.id === labId);
    if (!orderedLabs.includes(labId)) {
      setOrderedLabs([...orderedLabs, labId]);
      setPointsUsed(pointsUsed + labItem.cost);
    }
  };

  const handleOrderImaging = (imagingId) => {
    const imagingItem = currentCase.imaging.find(i => i.id === imagingId);
    if (!orderedImaging.includes(imagingId)) {
      setOrderedImaging([...orderedImaging, imagingId]);
      setPointsUsed(pointsUsed + imagingItem.cost);
    }
  };

  const handleSubmitDiagnosis = () => {
    const isCorrect = selectedDiagnosis === currentCase.correctDiagnosis;

    setCompletedCasesByDifficulty(prev => {
      const currentCompleted = prev[selectedDifficulty];
      if (currentCompleted.includes(currentCase.id)) {
        return prev;
      }
      return {
        ...prev,
        [selectedDifficulty]: [...currentCompleted, currentCase.id],
      };
    });

    if (isCorrect) {
      const difficulty = difficulties.find(d => d.id === selectedDifficulty);

      // Calculate efficiency bonus
      const essentialCost = currentCase.essentialFindings.reduce((sum, findingId) => {
        const historyItem = currentCase.history.find(h => h.id === findingId);
        if (historyItem) return sum + historyItem.cost;
        const physicalItem = currentCase.physical.find(p => p.id === findingId);
        if (physicalItem) return sum + physicalItem.cost;
        const labItem = currentCase.labs.find(l => l.id === findingId);
        if (labItem) return sum + labItem.cost;
        const imagingItem = currentCase.imaging.find(i => i.id === findingId);
        if (imagingItem) return sum + imagingItem.cost;
        return sum;
      }, 0);

      const efficiency = Math.max(0, 100 - Math.floor(((pointsUsed - essentialCost) / essentialCost) * 100));
      const bonusCoins = Math.floor((efficiency / 100) * difficulty.coins);

      const newUserData = {
        ...userData,
        coins: userData.coins + difficulty.coins + bonusCoins,
        xp: userData.xp + difficulty.xp,
        avatarStats: applyBoost(userData.avatarStats, GAME_BOOST),
      };
      saveUserData(newUserData);
    }

    setGamePhase("results");
  };

  const handleRestart = () => {
    setGamePhase("difficulty");
    setSelectedDifficulty(null);
    setOrderedCases([]);
    setCurrentCase(null);
    setCurrentCaseIndex(0);
    setRevealedHistory([]);
    setRevealedPhysical([]);
    setOrderedLabs([]);
    setOrderedImaging([]);
    setPointsUsed(0);
    setSelectedDiagnosis(null);
    setStartTime(null);
  };

  const handleNextCase = () => {
    if (orderedCases.length === 0) return;

    const nextIndex = (currentCaseIndex + 1) % orderedCases.length;

    setCurrentCaseIndex(nextIndex);
    setCurrentCase(orderedCases[nextIndex]);
    setRevealedHistory([]);
    setRevealedPhysical([]);
    setOrderedLabs([]);
    setOrderedImaging([]);
    setPointsUsed(0);
    setSelectedDiagnosis(null);
    setStartTime(Date.now());
    setGamePhase("case-intro");
  };

  const handleOrganSelect = (organSelection) => {
    const cases = medicalCases[selectedDifficulty];
    if (organSelection === "Random") {
      const shuffled = [...cases].sort(() => Math.random() - 0.5);
      startCaseSequence(shuffled, organSelection);
      return;
    }

    const filteredCases = cases.filter(caseItem => caseItem.organ === organSelection);
    if (filteredCases.length === 0) return;
    startCaseSequence(filteredCases, organSelection);
  };

  // Difficulty Selection Screen
  if (gamePhase === "difficulty") {
    return (
      <div
        className="min-h-screen"
        style={{
          backgroundImage: "url('/avatar-diagnose.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        <div className="min-h-screen bg-gradient-to-br from-indigo-900/80 to-cyan-900/80">
          <div className="max-w-5xl mx-auto px-4 py-8">
        <button
          onClick={onBack}
          className="mb-4 text-white hover:text-gray-200 flex items-center gap-2"
        >
          ← Back to Games
        </button>

        <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Diagnose the Patient
          </h1>
          <p className="text-gray-600">
            Work through a patient case systematically - gather history, perform examinations, order tests, and make your diagnosis
          </p>
        </div>

        <h2 className="text-2xl font-bold text-white mb-6">Select Difficulty</h2>

        <div className="grid md:grid-cols-3 gap-6">
          {difficulties.map((difficulty) => {
            const totalCases = medicalCases[difficulty.id].length;
            const completedCount = Math.min(
              completedCasesByDifficulty[difficulty.id]?.length || 0,
              totalCases
            );
            const progressPercent =
              totalCases > 0 ? (completedCount / totalCases) * 100 : 0;
            return (
              <button
                key={difficulty.id}
                onClick={() => handleDifficultySelect(difficulty.id)}
                className={
                  "rounded-xl p-6 border-2 text-left transition-all transform hover:shadow-xl hover:scale-105 bg-gradient-to-br " +
                  difficulty.color +
                  " " +
                  difficulty.borderColor
                }
              >
                <h3 className={"text-2xl font-bold mb-2 " + difficulty.textColor}>
                  {difficulty.name}
                </h3>
                <p className="text-sm text-gray-700 mb-4">
                  {difficulty.description}
                </p>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-semibold text-teal-600">
                    {difficulty.coins} coins
                  </span>
                  <span className="font-semibold text-purple-600">
                    {difficulty.xp} XP
                  </span>
                </div>
                <div className="mt-4">
                  <div className="h-2 bg-white/70 rounded-full">
                    <div
                      className="h-full bg-teal-600 rounded-full transition-all duration-300"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                  <div className="mt-1 text-xs text-gray-700 text-center">
                    {completedCount} / {totalCases} cases completed
                  </div>
                </div>
              </button>
            );
          })}
        </div>
          </div>
        </div>
      </div>
    );
  }

  // Organ Selection Screen
  if (gamePhase === "organ-select") {
    const cases = medicalCases[selectedDifficulty] || [];
    const organTotals = cases.reduce((acc, caseItem) => {
      acc[caseItem.organ] = (acc[caseItem.organ] || 0) + 1;
      return acc;
    }, {});
    const completed = completedCasesByDifficulty[selectedDifficulty] || [];
    const organCompleted = cases.reduce((acc, caseItem) => {
      if (completed.includes(caseItem.id)) {
        acc[caseItem.organ] = (acc[caseItem.organ] || 0) + 1;
      }
      return acc;
    }, {});
    const uniqueOrgans = Object.keys(organTotals).sort();

    return (
      <div
        className="min-h-screen"
        style={{
          backgroundImage: "url('/avatar-diagnose.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        <div className="min-h-screen bg-gradient-to-br from-indigo-900/80 to-cyan-900/80">
          <div className="max-w-5xl mx-auto px-4 py-8">
            <button
              onClick={handleRestart}
              className="mb-4 text-white hover:text-gray-200 flex items-center gap-2"
            >
              ← Change Difficulty
            </button>

            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 mb-6">
              <h1 className="text-3xl font-bold text-gray-800 mb-2">
                Choose an Organ Focus
              </h1>
              <p className="text-gray-600">
                Pick an organ for targeted cases or choose random order across all cases.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Progression</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {uniqueOrgans.map((organ) => {
                  const completedCount = organCompleted[organ] || 0;
                  const totalCount = organTotals[organ];
                  const isComplete = completedCount >= totalCount;
                  return (
                    <div
                      key={organ}
                      className={
                        "flex items-center justify-between rounded-lg px-4 py-3 border " +
                        (isComplete
                          ? "bg-green-50 border-green-300"
                          : "bg-gray-50 border-gray-200")
                      }
                    >
                      <span className={"font-semibold " + (isComplete ? "text-green-700" : "text-gray-800")}>
                        {organ}
                      </span>
                      <span className={"text-sm " + (isComplete ? "text-green-700" : "text-gray-600")}>
                        {completedCount} / {totalCount}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {uniqueOrgans.map((organ) => {
                const completedCount = organCompleted[organ] || 0;
                const totalCount = organTotals[organ];
                const isComplete = completedCount >= totalCount;
                return (
                  <button
                    key={organ}
                    onClick={() => handleOrganSelect(organ)}
                    className={
                      "rounded-xl p-6 border-2 text-left transition-all transform hover:shadow-xl hover:scale-105 " +
                      (isComplete
                        ? "bg-green-50 border-green-300"
                        : "bg-white border-gray-200")
                    }
                  >
                    <h3
                      className={
                        "text-2xl font-bold mb-2 " +
                        (isComplete ? "text-green-700" : "text-gray-800")
                      }
                    >
                      {organ}
                    </h3>
                    <p className={"text-sm " + (isComplete ? "text-green-700" : "text-gray-600")}>
                      {completedCount} / {totalCount} completed
                    </p>
                  </button>
                );
              })}
              <button
                onClick={() => handleOrganSelect("Random")}
                className="rounded-xl p-6 border-2 text-left transition-all transform hover:shadow-xl hover:scale-105 bg-gradient-to-br from-slate-100 to-blue-100 border-blue-200"
              >
                <h3 className="text-2xl font-bold mb-2 text-blue-700">Random Order</h3>
                <p className="text-sm text-gray-700">Mix all organs in a shuffled sequence</p>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Case Introduction Screen
  if (gamePhase === "case-intro") {
    return (
      <div
        className="min-h-screen"
        style={{
          backgroundImage: "url('/avatar-diagnose.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        <div className="min-h-screen bg-gradient-to-br from-indigo-900/80 to-cyan-900/80">
          <div className="max-w-4xl mx-auto px-4 py-8">
        <button
          onClick={() => setGamePhase("organ-select")}
          className="mb-4 text-white hover:text-gray-200 flex items-center gap-2"
        >
          ← Change Organ
        </button>

        <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Patient Case</h2>
              <p className="text-sm text-gray-600">{currentCase.specialty}</p>
            </div>
            <div className="flex items-center gap-2 text-teal-600">
              <Coins className="w-5 h-5" />
              <span className="font-semibold">Budget: 200 points</span>
            </div>
          </div>

          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 mb-6">
            <h3 className="font-bold text-gray-800 mb-3">Chief Complaint</h3>
            <p className="text-gray-700 text-lg mb-4">{currentCase.chiefComplaint}</p>

            <h4 className="font-semibold text-gray-700 mb-2">Initial Presentation:</h4>
            <ul className="space-y-2">
              {currentCase.initialPresentation.map((item, index) => (
                <li key={index} className="flex items-start gap-2 text-gray-700">
                  <ChevronRight className="w-4 h-4 mt-1 text-blue-600 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <button
            onClick={() => setGamePhase("history")}
            className="w-full bg-teal-600 text-white py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors"
          >
            Begin Diagnostic Workup
          </button>
        </div>
          </div>
        </div>
      </div>
    );
  }

  // Results Screen
  if (gamePhase === "results") {
    const isCorrect = selectedDiagnosis === currentCase.correctDiagnosis;
    const difficulty = difficulties.find(d => d.id === selectedDifficulty);
    const timeElapsed = Math.floor((Date.now() - startTime) / 1000);

    return (
      <div
        className="min-h-screen"
        style={{
          backgroundImage: "url('/avatar-diagnose.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        <div className="min-h-screen bg-gradient-to-br from-indigo-900/80 to-cyan-900/80">
          <div className="max-w-4xl mx-auto px-4 py-8">
        <button
          onClick={onBack}
          className="mb-4 text-white hover:text-gray-200 flex items-center gap-2"
        >
          ← Back to Games
        </button>

        <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
          <div className="text-center mb-6">
            {isCorrect ? (
              <>
                <Award className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h2 className="text-3xl font-bold text-green-600 mb-2">
                  Correct Diagnosis!
                </h2>
              </>
            ) : (
              <>
                <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <h2 className="text-3xl font-bold text-red-600 mb-2">
                  Incorrect Diagnosis
                </h2>
              </>
            )}
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 mb-6">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <div className="text-sm text-gray-600">Your Diagnosis</div>
                <div className="text-lg font-bold text-gray-800">{selectedDiagnosis}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Correct Diagnosis</div>
                <div className="text-lg font-bold text-green-600">{currentCase.correctDiagnosis}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Points Used</div>
                <div className="text-lg font-bold text-teal-600">{pointsUsed}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Time Taken</div>
                <div className="text-lg font-bold text-purple-600">
                  {Math.floor(timeElapsed / 60)}:{(timeElapsed % 60).toString().padStart(2, '0')}
                </div>
              </div>
            </div>
            {isCorrect && (
              <div className="text-sm text-gray-600">
                You earned {difficulty.coins} coins and {difficulty.xp} XP!
              </div>
            )}
          </div>

          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <h3 className="font-bold text-gray-800 mb-3">Explanation</h3>
            <p className="text-gray-700">{currentCase.explanation}</p>
          </div>

          <div className="space-y-3">
            <button
              onClick={handleNextCase}
              className="w-full bg-teal-600 text-white py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors"
            >
              Next Case
            </button>
            <button
              onClick={onBack}
              className="w-full bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
            >
              Back to Games
            </button>
          </div>
        </div>
          </div>
        </div>
      </div>
    );
  }

  // Main Diagnostic Workup Interface
  return (
    <div
      className="min-h-screen"
      style={{
        backgroundImage: "url('/avatar-diagnose.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      <div className="min-h-screen bg-gradient-to-br from-indigo-900/80 to-cyan-900/80">
        <div className="max-w-6xl mx-auto px-4 py-8">
      <button
        onClick={handleRestart}
        className="mb-4 text-white hover:text-gray-200 flex items-center gap-2"
      >
        ← Restart Case
      </button>

      {/* Header with case info and stats */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-800">{currentCase.chiefComplaint}</h2>
            <p className="text-sm text-gray-600">{currentCase.specialty}</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Coins className="w-5 h-5 text-teal-600" />
              <span className="font-semibold text-gray-700">{pointsUsed} / 200</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-purple-600" />
              <span className="font-semibold text-gray-700">
                {Math.floor((Date.now() - startTime) / 60000)}:{Math.floor(((Date.now() - startTime) % 60000) / 1000).toString().padStart(2, '0')}
              </span>
            </div>
          </div>
        </div>

        {/* Phase navigation */}
        <div className="flex gap-2">
          <button
            onClick={() => setGamePhase("history")}
            className={
              "px-4 py-2 rounded-lg font-semibold transition-colors " +
              (gamePhase === "history"
                ? "bg-teal-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200")
            }
          >
            History
          </button>
          <button
            onClick={() => setGamePhase("physical")}
            className={
              "px-4 py-2 rounded-lg font-semibold transition-colors " +
              (gamePhase === "physical"
                ? "bg-teal-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200")
            }
          >
            Physical Exam
          </button>
          <button
            onClick={() => setGamePhase("labs")}
            className={
              "px-4 py-2 rounded-lg font-semibold transition-colors " +
              (gamePhase === "labs"
                ? "bg-teal-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200")
            }
          >
            Labs
          </button>
          <button
            onClick={() => setGamePhase("imaging")}
            className={
              "px-4 py-2 rounded-lg font-semibold transition-colors " +
              (gamePhase === "imaging"
                ? "bg-teal-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200")
            }
          >
            Imaging
          </button>
          <button
            onClick={() => setGamePhase("diagnosis")}
            className={
              "px-4 py-2 rounded-lg font-semibold transition-colors " +
              (gamePhase === "diagnosis"
                ? "bg-teal-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200")
            }
          >
            Diagnosis
          </button>
        </div>
      </div>

      {/* History Taking Phase */}
      {gamePhase === "history" && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Detailed History</h3>
          <p className="text-gray-600 mb-6">
            Select specific history questions to ask the patient. Each question costs points.
          </p>

          <div className="space-y-3">
            {currentCase.history.map((item) => {
              const isRevealed = revealedHistory.includes(item.id);
              return (
                <button
                  key={item.id}
                  onClick={() => handleRevealHistory(item.id)}
                  disabled={isRevealed}
                  className={
                    "w-full p-4 rounded-lg border-2 text-left transition-all " +
                    (isRevealed
                      ? "bg-green-50 border-green-300"
                      : "bg-gray-50 border-gray-200 hover:border-teal-300 hover:bg-teal-50")
                  }
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {isRevealed ? (
                        <div className="flex items-start gap-2">
                          <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-800">{item.text}</span>
                        </div>
                      ) : (
                        <span className="text-gray-600">Click to reveal history item</span>
                      )}
                    </div>
                    <span className="ml-4 font-semibold text-teal-600">{item.cost} pts</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Physical Examination Phase */}
      {gamePhase === "physical" && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Physical Examination</h3>
          <p className="text-gray-600 mb-6">
            Perform specific physical examination maneuvers. Each examination costs points.
          </p>

          <div className="space-y-3">
            {currentCase.physical.map((item) => {
              const isRevealed = revealedPhysical.includes(item.id);
              return (
                <button
                  key={item.id}
                  onClick={() => handleRevealPhysical(item.id)}
                  disabled={isRevealed}
                  className={
                    "w-full p-4 rounded-lg border-2 text-left transition-all " +
                    (isRevealed
                      ? "bg-green-50 border-green-300"
                      : "bg-gray-50 border-gray-200 hover:border-teal-300 hover:bg-teal-50")
                  }
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {isRevealed ? (
                        <div className="flex items-start gap-2">
                          <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-800">{item.text}</span>
                        </div>
                      ) : (
                        <span className="text-gray-600">Click to perform examination</span>
                      )}
                    </div>
                    <span className="ml-4 font-semibold text-teal-600">{item.cost} pts</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Laboratory Tests Phase */}
      {gamePhase === "labs" && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Laboratory Tests</h3>
          <p className="text-gray-600 mb-6">
            Order laboratory tests to gather more diagnostic information. Each test costs points.
          </p>

          <div className="space-y-3">
            {currentCase.labs.map((item) => {
              const isOrdered = orderedLabs.includes(item.id);
              return (
                <button
                  key={item.id}
                  onClick={() => handleOrderLab(item.id)}
                  disabled={isOrdered}
                  className={
                    "w-full p-4 rounded-lg border-2 text-left transition-all " +
                    (isOrdered
                      ? "bg-green-50 border-green-300"
                      : "bg-gray-50 border-gray-200 hover:border-teal-300 hover:bg-teal-50")
                  }
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {isOrdered ? (
                        <div className="flex items-start gap-2">
                          <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-800">{item.text}</span>
                        </div>
                      ) : (
                        <span className="text-gray-600">Click to order test</span>
                      )}
                    </div>
                    <span className="ml-4 font-semibold text-teal-600">{item.cost} pts</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Imaging Studies Phase */}
      {gamePhase === "imaging" && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Imaging Studies</h3>
          <p className="text-gray-600 mb-6">
            Order imaging studies to visualize anatomy and pathology. Each study costs points.
          </p>

          <div className="space-y-3">
            {currentCase.imaging.map((item) => {
              const isOrdered = orderedImaging.includes(item.id);
              return (
                <button
                  key={item.id}
                  onClick={() => handleOrderImaging(item.id)}
                  disabled={isOrdered}
                  className={
                    "w-full p-4 rounded-lg border-2 text-left transition-all " +
                    (isOrdered
                      ? "bg-green-50 border-green-300"
                      : "bg-gray-50 border-gray-200 hover:border-teal-300 hover:bg-teal-50")
                  }
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {isOrdered ? (
                        <div className="flex items-start gap-2">
                          <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-800">{item.text}</span>
                        </div>
                      ) : (
                        <span className="text-gray-600">Click to order imaging</span>
                      )}
                    </div>
                    <span className="ml-4 font-semibold text-teal-600">{item.cost} pts</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Diagnosis Phase */}
      {gamePhase === "diagnosis" && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Make Your Diagnosis</h3>
          <p className="text-gray-600 mb-6">
            Based on the information you've gathered, select the most likely diagnosis.
          </p>

          <div className="space-y-3 mb-6">
            {currentCase.differentialDiagnoses.map((diagnosis) => (
              <button
                key={diagnosis}
                onClick={() => setSelectedDiagnosis(diagnosis)}
                className={
                  "w-full p-4 rounded-lg border-2 text-left transition-all " +
                  (selectedDiagnosis === diagnosis
                    ? "bg-teal-50 border-teal-400"
                    : "bg-gray-50 border-gray-200 hover:border-teal-300")
                }
              >
                <div className="flex items-center gap-3">
                  <div
                    className={
                      "w-5 h-5 rounded-full border-2 flex items-center justify-center " +
                      (selectedDiagnosis === diagnosis
                        ? "border-teal-600 bg-teal-600"
                        : "border-gray-300")
                    }
                  >
                    {selectedDiagnosis === diagnosis && (
                      <Check className="w-3 h-3 text-white" />
                    )}
                  </div>
                  <span className="font-semibold text-gray-800">{diagnosis}</span>
                </div>
              </button>
            ))}
          </div>

          <button
            onClick={handleSubmitDiagnosis}
            disabled={!selectedDiagnosis}
            className={
              "w-full py-3 rounded-lg font-semibold transition-colors " +
              (selectedDiagnosis
                ? "bg-teal-600 text-white hover:bg-teal-700"
                : "bg-gray-300 text-gray-500 cursor-not-allowed")
            }
          >
            Submit Diagnosis
          </button>
        </div>
      )}
        </div>
      </div>
    </div>
  );
};

export default DiagnosePatient;
